import { SHELTER_CLUE_ITEMS } from "./ember_clues"
import { locateInventoryActiveItem } from "game-common/item/item"
import { Mechanics } from "game-common/mechanics/mechanics"
import {
    DynamicTextMeta,
    EmberClueType,
    Entity,
    EntityId,
    EquipSlot,
    ItemDropTarget,
    ItemDetailMeta,
    ItemDetailOption,
    MAX_READIED_COUNT,
    PlayerEvent,
    QuantityPrompt,
    ScreenMessage,
} from "game-common/models"
import { Callback } from "game-common/util"
import { Container, Graphics, InteractionEvent, LINE_CAP, LINE_JOIN, Point, Rectangle, Text } from "pixi.js"

import { BUTTON_SIZES } from "./ui_tokens"
import { chamfer, groove, rivet, RIM, RIM_LIGHT, TIMBER, TIMBER_EDGE } from "./build_board"
import { drawWheelIcon, WHEEL_ICON_BOX, WheelIcon } from "./command_wheel_icons"
import { TextRole } from "../text/typography"
import { ClientGameLogic } from "../../../../client_game_logic"
import { stageViewport } from "../../../../canvas_layout"
import { isMobile } from "../../../../client_util"
import { GuiManager } from "../gui_manager"
import { BasicSprite } from "../sprites/basic_sprite"
import { centerSpriteIn } from "./center_sprite"
import { fitItemIcon, PACK_SLOT } from "./fit_item_icon"
import { CRAFTING_GROUP_LABELS, CRAFTING_GROUPS } from "game-common/item/crafting_groups"
import {
    BenchRecipe,
    buildCatalog,
    CatalogGroup,
    CatalogTile,
    enabledKeys,
    familyOf,
    firstStep,
    isShort,
    needLine,
    newlyEnabled,
    READY_GROUP,
    READY_LABEL,
    recipeForItem,
    sourceLine,
} from "./crafting_catalog"
import {
    activeGroup,
    BENCH_ACTION_H,
    BENCH_ING,
    BENCH_PAD,
    BENCH_ROW_GAP,
    BENCH_TOP_H,
    BENCH_WELL,
    clampScroll,
    craftingRegions,
    GridGroupBox,
    GridLayout,
    GridLine,
    HEADER_H,
    layoutGrid,
    maxScrollFor,
    pinnedLine,
    Rect,
    scrollForGroup,
    scrollToShow,
    TILE,
    tileAt,
    tileVisible,
} from "./crafting_grid"
import {
    BADGE_MS,
    FLIGHT_MS,
    FLIGHT_STAGGER_MS,
    flightAt,
    glowAlpha,
    GLOW_MS,
    HOLD_FIRST_MS,
    MAX_BATCH_FLIGHTS,
    nextHoldDelay,
    POP_MS,
    popScale,
    sparkAt,
} from "./crafting_fx"
import { disposeTextTextures } from "../text/dispose_text"
import { getRendererApp } from "../renderer_app"
import { OverlayRect, overlayRect, SearchField } from "./search_field"
import { TextRenderer } from "../text/text_renderer"
import { DynamicDialog } from "./dynamic_dialog"
import { EntityDisplayer } from "./entity_displayer"
import { Fader } from "./fader"
import { drawConditionGauge, GAUGE_HEIGHT, conditionColor, WearBar } from "./durability"
import { Modal } from "./modal"
import { UiButton } from "./ui_button"
import { createButton } from "./button_factory"
import {
    ACTION_H,
    ACTION_Y,
    applyBackspace,
    applyDigit,
    bufferQuantity,
    CANCEL_W,
    CHIP_GAP,
    CHIP_H,
    CHIP_ROW_X,
    CHIP_W,
    CHIP_Y,
    clampQuantity,
    CONFIRM_W,
    CONFIRM_X,
    halfOf,
    HANDLE_R,
    offsetForQuantity,
    PICKER_H,
    PICKER_PAD,
    PICKER_W,
    pickerOrigin,
    quantityAtOffset,
    READOUT_GAP,
    READOUT_H,
    READOUT_W,
    READOUT_Y,
    STEP_SIZE,
    STEP_Y,
    TITLE_Y,
    TRACK_CENTER_Y,
    TRACK_H,
    TRACK_W,
    TRACK_X,
} from "./quantity_picker"
import { Throbber } from "./throbber"
import { clueSatisfiedByDrawing } from "./ember_clues"
import { namePillLayout } from "./name_pill"
import { closeControlRadius, getUiMetrics } from "./ui_metrics"
import { ItemSlotDrag, SocketPrime } from "./item_slot_drag"
import { PackProfileCard, PROFILE_CARD_H, PROFILE_CARD_W } from "./pack_profile_card"
import { RosterDamageFx } from "./roster_damage"

// -----------------------------------------------------------------------------
// Palette -- the camp's own materials.
//
// These screens used to be a cool slate "dark theme" (navy plate, blue-grey rims, a blue readied
// ring, a green HP bar), which was fine on its own and wrong next to everything around it: the HUD,
// the wave dial, the build board and the command wheel are all cut timber, forged bronze and
// firelight, so opening your pack swapped one game's furniture for another's. The panel is now a
// timber board (build_board.ts), item slots are sockets carved into it, and every accent is a warm
// one: gold for "this is the one", bronze for equipped, ember for readied.
// -----------------------------------------------------------------------------
const COLOR = {
    panelFill: 0x16110c, // the board's face (build_board FACE, a hair warmer)
    panelFillTop: 0x241a11, // the title band, lit a little from the fire
    panelBorder: 0x4a3c2c, // hairlines and rules cut into the face
    gold: 0xf0b453, // accents: hover rings, the drop highlight, the scroll thumb
    equipped: 0xd9b483, // build_board RIM_LIGHT -- worn, so it is the metal
    readied: 0xf28c4e, // in hand, so it is the fire
    hp: 0xc9523b,
    slotFill: 0x271d14, // a socket with something in it
    slotEmpty: 0x120d09, // an empty socket: recessive, so the pack reads by its contents
    slotInset: 0x0a0705, // the cut itself
    slotBorder: 0x3a2f23,
    slotHover: 0xffd166, // build_board GOLD
    text: 0xffedc7, // build_board INK
    textMuted: 0xae9a7a,
    title: 0xf3d9a4, // panel titles and names
    label: 0xd9b483, // section labels, in the bronze of the rim
    shortfall: 0xec7a5c, // an ingredient the player is short of
    cardFill: 0x18120d,
    cardBorder: 0x8e7b58, // build_board RIM
}

// ---- the board's surfaces ----------------------------------------------------
//
// Three shapes do almost all the drawing below, so the panel cannot drift into using three slightly
// different versions of each.

// Clipped corners rather than rounded ones, as on the build board: a radius is a desktop dialog, a
// chamfer is a sawn plank.
const PANEL_CHAMFER = 9
const SOCKET_CHAMFER = 3
const PLATE_CHAMFER = 4

/**
 * A socket carved into the board: the dark cut, a face set slightly into it, and a hairline of light
 * along the cut's lower lip -- light from above catches the far edge of a hole, which is what makes it
 * read as recessed rather than painted on.
 */
const drawSocket = (g: Graphics, x: number, y: number, w: number, h: number, face: number = COLOR.slotFill,
    c: number = SOCKET_CHAMFER) => {
    g.lineStyle(0)
    g.beginFill(COLOR.slotInset, 1)
    chamfer(g, x, y, w, h, c)
    g.endFill()
    g.beginFill(face, 1)
    chamfer(g, x + 1.5, y + 2, w - 3, h - 3.5, Math.max(1, c - 1))
    g.endFill()
    g.lineStyle(1, RIM_LIGHT, 0.1)
    g.moveTo(x + c, y + h - 0.5).lineTo(x + w - c, y + h - 0.5)
    g.lineStyle(0)
}

/** A plate laid ON the board -- a card, a menu, a label: dropped shadow, dark face, bronze rim, lit top. */
const drawPlate = (g: Graphics, x: number, y: number, w: number, h: number,
    { face = COLOR.cardFill, faceAlpha = 0.97, rim = COLOR.cardBorder, rimAlpha = 0.85, c = PLATE_CHAMFER,
        shadow = true }: { face?: number; faceAlpha?: number; rim?: number; rimAlpha?: number; c?: number;
        shadow?: boolean } = {}) => {
    g.lineStyle(0)
    if (shadow) {
        g.beginFill(0x000000, 0.38)
        chamfer(g, x, y + 2, w, h, c)
        g.endFill()
    }
    g.lineStyle({ width: 1, color: rim, alpha: rimAlpha, join: LINE_JOIN.MITER })
    g.beginFill(face, faceAlpha)
    chamfer(g, x, y, w, h, c)
    g.endFill()
    g.lineStyle(1, RIM_LIGHT, 0.12)
    g.moveTo(x + c, y + 1.5).lineTo(x + w - c, y + 1.5)
    g.lineStyle(0)
}

/** A glyph from the command wheel's set (command_wheel_icons.ts), at `size`, centred on (x, y). */
const glyph = (icon: WheelIcon, x: number, y: number, size: number, color: number): Graphics => {
    const g = new Graphics()
    drawWheelIcon(g, icon)
    g.scale.set(size / WHEEL_ICON_BOX)
    g.position.set(x, y)
    g.tint = color
    return g
}

// Letterspaced caps for titles and section labels: the dial's and the wheel's banner voice.
const caps = (value: string, role: TextRole, color: number, spacing: number): Text => {
    const t = text(value.toUpperCase(), role, color, true)
    t.style.letterSpacing = spacing
    return t
}

// Fallback content dimensions for messages outside the docked inventory renderer.
const PANEL_W = 780
const PANEL_H = 442

// Right-docked panel used for the inventory/crafting/trade screens: a tall, narrow panel pinned to
// the right so the player and the world stay visible on the left. DOCK_X is the on-screen left
// offset applied to the whole content container. The width is fitted to the 8-column grid
// (8*32 + 7*4 = 284) plus even 16px margins, and DOCK_X leaves room for trade's close button, which
// hangs off the panel's top-right corner.
const DOCK_SLOT = PACK_SLOT // compact slot size for the narrow docked grids
// The header row's buttons: their top and height, and the right margin they end at. Inventory and
// crafting seat their close disc at the end of this row (see headerCloseRoom).
const HEADER_BUTTON_Y = 11
const HEADER_BUTTON_H = 24
const HEADER_RIGHT_MARGIN = 16
const DOCK_MARGIN = 16
const DOCK_W = DOCK_MARGIN * 2 + 8 * DOCK_SLOT + 7 * 4 // 316
const MOBILE_PANEL_W = DOCK_W * 2
const DOCK_H = 430
const DOCK_X = 452
const MOBILE_PANEL_SCALE = isMobile() ? 1.331 : 1
// Grow toward the world while keeping the panel's right edge in the same place.
const SCALED_DOCK_X = DOCK_X + DOCK_W * (1 - MOBILE_PANEL_SCALE)
// The panel background is drawn inset by this much inside the content box, so this is where its
// visible corners are (see drawPanelBg).
const PANEL_INSET = 4
// Slack assemble() leaves under the content when it sizes the panel to fit. A screen that lays itself
// out against a height budget (crafting) has to subtract it to land inside DOCK_H.
const PANEL_BOTTOM_PAD = 14

const SLOT = 44
const GAP = 6

// ---- the profile row --------------------------------------------------------------------------------
//
// The player's card (pack_profile_card.ts) between two columns of gear sockets: what is worn down the
// left in body order, the hands on the right. Each socket takes only its own kind of item -- the
// server stamps that on every item as `equipSlot` -- and a drop anywhere else in the row finds the
// right socket by itself.
const SOCKET = 36
const SOCKET_GAP = 6
const PROFILE_Y = 48
const LEFT_SOCKETS: EquipSlot[] = ["armor", "shirt", "pants", "shoes"]
const RIGHT_SOCKETS: EquipSlot[] = ["weapon", "shield"]
const PROFILE_CARD_X = DOCK_MARGIN + SOCKET + 10
const READIED_LABEL_Y = PROFILE_Y + PROFILE_CARD_H + 10
const BACKPACK_LABEL_Y = READIED_LABEL_Y + 56
// What each empty socket shows, faintly: the outline of something that goes in it, from its own art.
const SOCKET_GHOSTS: Record<EquipSlot, string> = {
    armor: "armor-leather-brown.png",
    shirt: "longsleeve_laced-white.png",
    pants: "pants-brown.png",
    shoes: "boots-brown.png",
    weapon: "longsword-metal.png",
    shield: "shield-round-brown.png",
}
const SOCKET_NAMES: Record<EquipSlot, string> = {
    armor: "Armor", shirt: "Shirt", pants: "Pants", shoes: "Shoes", weapon: "Hand", shield: "Shield",
}
// Hover an item and press its number to send it to that ready slot (Minecraft's hotbar keys).
const READY_KEY = /^[1-6]$/
// The id the profile card's hit effect is filed under.
const PROFILE_FX_ID = "pack-profile"

/** What a slot needs beyond its item: the gear socket it is, or the ready slot number it shows. */
interface SlotExtras {
    socket?: EquipSlot
    // The socket's item is worn gear (it came from the equipped grid), not one shown from the ready bar.
    worn?: boolean
    readyNumber?: number
}

// ---- the ember tutorial's clues -------------------------------------------------------------
//
// Three of the five controls the first-run camp's tutorial points at live in THIS panel: the Craft
// toggle in the inventory's title bar, the campfire's own Craft button in the crafting list, and the
// crafted campfire's slot in the pack (whose hover menu carries the fourth, its Use button). The
// panel does not know or care whether a clue is armed -- it simply reports where it drew each one,
// and the tutorial layer decides (see gui/tutorial.ts registerClueAnchor).
//
// Where a clued control was drawn, in panel coordinates, and what the spotlight should leave lit
// round it when that is more than the control itself.
interface PanelClueAnchor {
    x: number
    y: number
    w: number
    h: number
    reveal?: { x: number; y: number; w: number; h: number }
    // The note's own words and side, when this control says the step differently (see ClueAnchor).
    lines?: string[]
    placement?: "left" | "above" | "below"
}

// Every clue this panel owns, so a rebuild or a close can retract the lot in one go.
const PANEL_CLUES: EmberClueType[] = ["ember-planks-recipe", "ember-wall-recipe", "ember-wall-pack", "ember-wall-use", "ember-roof-recipe", "ember-roof-pack", "ember-roof-use", "ember-crafting", "ember-recipe", "ember-pack", "ember-use", "ember-close", "ember-wood-pack", "ember-wood-use"]

// The recipe and the item the clues name. `title` is capitalize(itemTypeMeta.type) server-side and
// a recipe's `name` is the raw item type, so both compare case-insensitively against this.
const CLUED_ITEM = "campfire"
// The inventory title bar's action that opens the crafting screen.
const CRAFT_ACTION = /craft/i
// The hover-menu option that puts a carried campfire on the ground (inventory.ts pushes "Use" for
// anything `useable`, which is how a campfire is placed -- it is not buildable).
const READY_OPTION = /^ready$/i
// The hover name pill. Small, quiet, and gone the moment the pointer leaves: its whole job is to
// answer "what is that?" without opening anything.
const PILL_PAD_X = 7
const PILL_PAD_Y = 3
const PILL_FILL = 0x1b1512
const PILL_BORDER = 0x8e7b58
const PILL_TEXT = 0xffedc7

interface ParsedInventory {
    backpack: ItemDetailMeta[]
    readied: ItemDetailMeta[]
    equipped: ItemDetailMeta[]
    entity?: { entityId: EntityId; appearance?: any; inventory?: any }
    name?: string
    statLines: string[]
    actions: { text: string; event: PlayerEvent }[]
    gridCount: number
    // Signature of the self-inventory panel: a 6x5 backpack grid. Sub-dialogs that reuse this
    // modal (crafting, trades) render through genDynamicText too, so this alone isn't enough —
    // see isSelfInventory() which also requires the character portrait.
    hasBackpackGrid: boolean
}

const text = (value: string, role: TextRole, color: number, bold = false): Text => {
    const renderer = new TextRenderer().role(role).bold(bold).color(color)
    return renderer.render(value) as Text
}

/** The HP the screen was drawn with, from its "HP: 37 / 50" stat line. */
const parseHp = (statLines: string[]): { hp: number; max: number } | undefined => {
    for (const line of statLines) {
        const hp = line.match(/^HP:\s*([\d.]+)\s*\/\s*([\d.]+)/)
        if (hp) return { hp: parseFloat(hp[1]), max: parseFloat(hp[2]) }
    }
    return undefined
}

const prettify = (value: string): string =>
    (value || "").replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase())

// The scrollbar beside a recipe list that does not fit, and how far a press may wander before it is
// a scroll rather than a tap on the card underneath.
//
// 10px, not the 4 this started at: at four it read as a decoration and could not be gripped, which is
// the one job a scrollbar has beyond saying there is more. The thumb never shrinks below a thumb's
// worth of height either, however long the list gets.
const SCROLLBAR_W = 10
// How far the search field's text starts in from the box's left edge, in box heights: clear of the
// magnifier drawn there.
const SEARCH_GLYPH_INSET = 0.75
// The panel's entrance: a slide in off its dock on opening, and a shorter, shallower one on turning
// from one of its screens to another (pack -> crafting). Short enough never to stand between a
// player and their pack.
const ENTRANCE_MS = 170
const ENTRANCE_SLIDE = 16
const TURN_MS = 130
const TURN_FROM_ALPHA = 0.35
const TURN_SLIDE = 6
const SCROLLBAR_MIN_THUMB = 24
const DRAG_SLOP = 4

// The crafting grid's scroll eases to a group when its rail tab is tapped, rather than jumping.
const SCROLL_TWEEN_MS = 220
// The mobile panel's crafting columns end here (it is shorter than the dock).
const MOBILE_CRAFT_BOTTOM = 280
// A tile that is selected: lit from the fire, under its gold rim.
const SELECTED_FACE = 0x3a2b1c
// An item the player cannot make yet, laid flat and warm-grey.
const DIM_ICON_TINT = 0xa3927a
// The rail tabs' side padding.
const RAIL_TAB_PAD = 5
// Sparks thrown off the bench by a craft.
const SPARK_COUNT = 8
// The quick flash on the tile a made item lands on.
const LANDING_GLOW_MS = 450
// The glow key for the bench's Craft button, beside the families' own.
const CRAFT_BUTTON_GLOW = "__craft"
// The tutorial's in-the-pack clues, pointed at the crafting screen's Pack tab once the item is made.
const PACK_TAB_CLUES: { itemType: string; type: EmberClueType; lines: string[] }[] = [
    { itemType: "campfire", type: "ember-pack", lines: ["Open your pack.", "The campfire you made is in there."] },
    { itemType: "wall-wood", type: "ember-wall-pack", lines: ["Open your pack.", "Your walls are in there."] },
    { itemType: "roof-wood", type: "ember-roof-pack", lines: ["Open your pack.", "Your roof sections are in there."] },
]

// Line heights per text role, measured once: what clampedText counts lines in.
const lineHeights = new Map<TextRole, number>()
const lineHeightOf = (role: TextRole): number => {
    let h = lineHeights.get(role)
    if (h === undefined) {
        const probe = text("Ag", role, 0xffffff)
        h = probe.height
        disposeTextTextures(probe)
        lineHeights.set(role, h)
    }
    return h
}

/** Wrapped text cut to `lines` lines at most, ending in an ellipsis when it had to be cut. */
const clampedText = (value: string, role: TextRole, color: number, width: number, lines: number): Text => {
    const render = (v: string) => new TextRenderer().role(role).color(color).wordWrap(Math.max(10, Math.floor(width))).render(v) as Text
    const maxH = lineHeightOf(role) * lines + 2
    let words = value.split(/\s+/)
    let t = render(value)
    while (t.height > maxH && words.length > 1) {
        disposeTextTextures(t)
        words = words.slice(0, -1)
        t = render(`${words.join(" ")}…`)
    }
    return t
}

// -----------------------------------------------------------------------------
// Crafting payload model. The crafting screen reuses the inventory modal; its
// dynamicText is one flat list of recipe rows, each carrying the recipe as data.
// -----------------------------------------------------------------------------
// A recipe as the bench knows it, with the sealed event its Craft fires.
interface CraftRecipe extends BenchRecipe {
    event?: PlayerEvent
}

interface ParsedCrafting {
    recipes: CraftRecipe[]
    headerActions: { text: string; event: PlayerEvent }[]
    empty: boolean
    // "Show them this recipe." A one-shot from the server -- the ember tutorial pointing at the
    // campfire. The panel selects it and brings its tile into view, because only the panel knows
    // where that tile currently is.
    reveal?: string
}

// Where a recipe's tile was drawn this pass: in the grid's own (scrolling) coordinates, with the
// nodes the motion touches between rebuilds.
interface PlacedTile {
    x: number
    y: number
    group: string
    itemType: string
    glow: Graphics
    count?: Text
}

// One made item in the air, from the bench to wherever it lands (looked up every frame).
interface CraftFlight {
    node: Container
    from: { x: number; y: number }
    start: number
    size: number
    target: { family: string } | { ingredient: string }
    itemType: string
    // How many items this landing puts in the pack.
    landed: number
}

interface CraftSpark {
    node: Graphics
    origin: { x: number; y: number }
    angle: number
    reach: number
    start: number
    duration: number
}

const asMeta = (node: DynamicTextMeta | string): DynamicTextMeta =>
    typeof node === "string" ? { text: node } : node

/**
 * Strip the one-shot "show them this recipe" marker out of a payload, in place.
 *
 * The panel re-reads its stored message every time it redraws, and it redraws for things the server
 * knows nothing about: a scroll, a keystroke, opening the category dropdown. A marker left in that
 * message is therefore re-applied on every one of those, which is not what one-shot means -- opening
 * the dropdown set `open = true`, rebuilt, and the stale marker set it straight back to false.
 *
 * Returns whether it found one, so a caller can assert it only ever fires once.
 */
export const consumeRevealMarker = (nodes?: DynamicTextMeta[]): boolean => {
    let found = false
    ;(nodes || []).forEach(node => {
        if (node?.context?.craftingReveal) {
            delete node.context.craftingReveal
            found = true
        }
        if (consumeRevealMarker(node?.children)) {
            found = true
        }
    })
    return found
}

// "Craftable items" is a label the crafting atom always emits and nothing else does — a reliable,
// cheap discriminator for "this reuse of the modal is the crafting screen".
export const isCraftingMessage = (message: ScreenMessage): boolean =>
    (message.dynamicText || []).some(n => asMeta(n)?.text === "Craftable items")

// Does any node in this subtree carry exactly this text? Used to pick the recipe browser out of the
// payload, by the "Craft" button or the "None" placeholder it is sure to contain.
const hasDescendantText = (node: DynamicTextMeta | undefined, value: string): boolean => {
    if (!node) {
        return false
    }
    if (node.text === value) {
        return true
    }
    return (node.children || []).some(c => hasDescendantText(c, value))
}

// -----------------------------------------------------------------------------
// Two-sided transfer model (chest / trade). The server lays the two entities out
// by x-position: target (chest) on the left, origin (player) on the right, with a
// center "action-trade.png" arrow.
// -----------------------------------------------------------------------------
interface TradeSide {
    name?: string
    items: ItemDetailMeta[]
    entity?: { entityId: EntityId; appearance?: any; inventory?: any }
    spriteId?: string
    statLines: string[]
}

interface ParsedTrade {
    left: TradeSide
    right: TradeSide
    headerActions: { text: string; event: PlayerEvent }[]
}

// The transfer UI always includes the center "action-trade.png" arrow — a unique, reliable marker.
export const isTradeMessage = (message: ScreenMessage): boolean =>
    (message.dynamicText || []).some(n => {
        const m = asMeta(n)
        return m?.type === "sprite" && m?.spriteId === "action-trade.png"
    })

// -----------------------------------------------------------------------------
// The floating item detail / action card. Shown on hover (or tap on mobile); stays
// open while the pointer is over the originating slot OR the card itself, so its
// action buttons are reachable. Mirrors the classic ItemDetail's Fader behavior.
// -----------------------------------------------------------------------------
class ModernDetailCard extends Container {
    private surface: Graphics
    private fader: Fader

    // Told, once the menu is laid out, where each action button landed -- in the coordinates of the
    // container the card itself sits in, so the caller can point a tutorial clue at one. Set by the
    // dialog that owns the card; nothing else uses it.
    optionAnchors?: (item: ItemDetailMeta, anchors: { option: ItemDetailOption; rect: Rectangle }[]) => void
    // ...and when the menu has actually gone, so a clue pointing at one of its buttons goes with it
    // rather than hanging over empty panel. Fired after the fade, not on mouseout: the grace period is
    // exactly the window in which the pointer is travelling towards the button.
    onHidden?: () => void

    constructor() {
        super()
        this.surface = new Graphics()
        this.addChild(this.surface)
        this.visible = false
        this.interactive = true

        this.on("mouseover", this.keepOpen)
        this.on("mouseout", this.hideIfPointerOutside)
    }

    private keepOpen = () => {
        // Cancelling a fade must not run its hidden callback, which retracts the Ready clue.
        this.fader?.stop(true)
        this.visible = true
        this.alpha = 1.0
    }

    // With a hitArea on this container, Pixi tracks the child buttons separately and can report
    // that the card itself was exited as the pointer enters a button. Keep the menu alive for those
    // internal transitions; a button's own mouseout performs the same boundary check on the way out.
    private hideIfPointerOutside = (event?: InteractionEvent) => this.scheduleHide(event)

    private keepButtonHover = (button: UiButton) => {
        button.on("mouseover", this.keepOpen)
        button.on("mouseout", this.hideIfPointerOutside)
    }

    hideImmediately = () => {
        this.fader?.stop(true)
        this.visible = false
        this.onHidden?.()
    }

    scheduleHide = (event?: InteractionEvent) => {
        // Slot mouseout fires as the pointer travels from the item to this floating menu. Test the
        // destination from that event before starting the grace timer; the menu's hit area excludes
        // interactive child buttons in Pixi's hover tracking, so its own mouseover is not sufficient.
        if (event?.data?.global) {
            const point = this.toLocal(event.data.global)
            if (this.hitArea?.contains(point.x, point.y)) {
                this.keepOpen()
                return
            }
        }
        // Stop any in-flight fade first so overlapping faders can't stack and yank the card
        // invisible mid-interaction (that race made action buttons occasionally un-clickable).
        if (this.fader) {
            this.fader.stop(true)
        }
        // Gentle ~250ms grace so the pointer has time to travel from the slot onto the card.
        this.fader = new Fader(this, {
            fadeInterval: 16,
            fadeAlphaDecrement: 0.06,
            releaseCallback: () => {
                this.alpha = 1.0
                this.visible = false
                if (this.onHidden) {
                    this.onHidden()
                }
            },
        })
        this.fader.update()
    }

    show = (
        item: ItemDetailMeta,
        onSelect: Callback<ItemDetailOption>,
        anchorX: number,
        anchorY: number,
        anchorSize: number = SLOT,
        panelW: number = PANEL_W,
        panelH: number = PANEL_H,
    ) => {
        if (this.fader) {
            this.fader.stop(true)
        }
        // Free the previous card's Text textures before rebuilding (canvas Text textures leak
        // otherwise); shared item-sprite textures are untouched by disposeTextTextures.
        ;[...this.surface.children].forEach(disposeTextTextures)
        this.surface.removeChildren()
        this.surface.clear()

        const padX = 12
        const padY = 10
        const lineGap = 4
        let y = padY
        let contentW = 150

        const accent = item.active ? item.activeColor ?? COLOR.equipped : COLOR.gold
        const title = text(item.title || "Item", "label", COLOR.title, true)
        title.x = padX
        title.y = y
        this.surface.addChild(title)
        contentW = Math.max(contentW, title.width)
        y += title.height + lineGap + 2

        // CONDITION, if this thing wears. A labelled gauge rather than the muted "38% durable
        // (worn)" sentence this used to be the last line of: the number and the word are both worth
        // having, but what a player actually wants off this card is how much is LEFT, and a length
        // answers that before the text has been read. The slot's own gauge is the same object at
        // 4px (gui/durability.ts), so the card is a bigger look at a thing already seen, not a
        // second notation for it.
        let gauge: { y: number; figure: Text } | undefined
        if (item.condition !== undefined) {
            const pct = item.condition
            const label = text("CONDITION", "overline", COLOR.textMuted)
            label.x = padX
            label.y = y + 1
            this.surface.addChild(label)
            // Rounded UP off zero: an axe reported as "0%" is one a player will believe is broken
            // already, and it still has a swing in it.
            const figure = text(`${Math.max(1, Math.round(pct * 100))}% · ${
                prettify(Mechanics.item.durability.conditionForPct(pct))}`,
                "compactCaption", conditionColor(pct), true)
            figure.y = y
            this.surface.addChild(figure)
            // The row has to fit both ends with a gap between them, whatever the title measured.
            contentW = Math.max(contentW, label.width + 14 + figure.width)
            y += Math.max(label.height, figure.height) + 3
            // Placed once the card's final width is known, below -- a gauge is the one thing here
            // that must span the whole inner width, and a right-aligned figure needs the same
            // answer.
            gauge = { y, figure }
            y += GAUGE_HEIGHT + lineGap + 3
        }

        ;(item.hoverText || []).forEach(line => {
            const t = text(line, "compactLabel", COLOR.textMuted)
            t.x = padX
            t.y = y
            this.surface.addChild(t)
            contentW = Math.max(contentW, t.width)
            y += t.height + lineGap
        })

        const options = item.options || []
        if (options.length > 0) {
            y += 4
        }

        const btnH = 26
        const btnGap = 5
        const optionButtons: { option: ItemDetailOption; button: UiButton }[] = []
        options.forEach(option => {
            const btnW = () => contentW // resolved after final width known; recomputed below
            const button = makeButton(option.title, btnW(), btnH, () => {
                onSelect({ ...option })
                this.hideNow()
            })
            this.keepButtonHover(button)
            button.x = padX
            button.y = y
            this.surface.addChild(button)
            optionButtons.push({ option, button })
            y += btnH + btnGap
        })

        const cardW = contentW + padX * 2
        const cardH = y + padY - (options.length > 0 ? btnGap : 0)

        // Re-stretch buttons to the full card inner width now that it's known.
        this.surface.children.forEach(child => {
            if (child instanceof UiButton) {
                child.resize(cardW - padX * 2, btnH)
            }
        })

        // Backdrop: a plate laid over the pack, with the item's accent run along its top edge.
        drawPlate(this.surface, 0, 0, cardW, cardH, { c: 5 })
        this.surface.lineStyle(2, accent, 0.9)
        this.surface.moveTo(5, 1).lineTo(cardW - 5, 1)
        this.surface.lineStyle(0)

        // The condition gauge, which could not be drawn until the card's width was known -- and has
        // to be issued AFTER the backdrop, because it shares the backdrop's Graphics and that is a
        // single draw list rather than a stack of layers. (The text and buttons are children, so
        // they are above all of it whatever order it was issued in.)
        if (gauge) {
            gauge.figure.x = cardW - padX - gauge.figure.width
            drawConditionGauge(this.surface, item.condition!, padX, gauge.y, cardW - padX * 2)
        }
        // Keep the drawn backdrop behind the text/buttons.
        this.surface.setChildIndex(this.surface.children[0], 0)

        // A full-card hit area means hovering anywhere on the card (not just a button) keeps it
        // alive — moving the pointer from the slot onto the card immediately cancels the fade.
        this.hitArea = new Rectangle(0, 0, cardW, cardH)

        // Anchor as a dropdown directly beneath the slot, overlapping it by 2px so there is NO dead
        // gap for the pointer to cross on its way to the menu (crossing empty space is what let the
        // menu fade out before you could click it). Flip above / clamp to stay on-panel.
        let x = anchorX
        if (x + cardW > panelW - 8) {
            x = panelW - 8 - cardW
        }
        if (x < 8) {
            x = 8
        }
        let cy = anchorY + anchorSize - 2
        if (cy + cardH > panelH - 6) {
            cy = anchorY - cardH + 2 // not enough room below — flip above the slot
        }
        // Final guarantee: keep the WHOLE menu inside the panel so nothing is ever clipped — even a
        // tall menu near an edge. This pins the bottom edge in-bounds (it may overlap the icon), so
        // every option stays visible and clickable rather than running off the panel.
        cy = Math.max(8, Math.min(cy, panelH - 6 - cardH))
        this.x = x
        this.y = cy
        this.visible = true
        this.alpha = 1.0

        // Report the buttons last, once the card's own position is settled: a clue has to be told
        // where a button is in the PARENT's space, and until now neither the card's x/y nor the
        // buttons' final width were known.
        if (this.optionAnchors && optionButtons.length > 0) {
            this.optionAnchors(
                item,
                optionButtons.map(({ option, button }) => ({
                    option,
                    rect: new Rectangle(this.x + button.x, this.y + button.y, cardW - padX * 2, btnH),
                })),
            )
        }
    }

    private hideNow = () => {
        this.visible = false
        this.alpha = 1.0
        if (this.onHidden) {
            this.onHidden()
        }
    }
}

// Compatibility adapter for existing measured inventory layouts. State and interaction belong
// to UiButton; new controls can use its standard size tiers directly.
//
// Every button in these screens is the camp's cut-timber plate. `primary` is the one action a row or
// panel is FOR -- Craft, the transfer's confirm -- and wears a gold rim and bold caption so the eye
// lands on it first; everything else is a quiet bronze-rimmed plank.
const makeButton = (
    label: string,
    width: number,
    height: number,
    onClick: () => void,
    style?: { accent?: number; primary?: boolean },
): UiButton => createButton({
    label, width, height, onClick,
    size: height <= BUTTON_SIZES.extraSmall.height ? "extraSmall"
        : height <= BUTTON_SIZES.small.height ? "small" : "medium",
    theme: "ember", shape: "chamfer", fill: style?.accent,
    variant: style?.primary ? "primary" : "secondary",
    accent: style?.primary ? COLOR.slotHover : undefined,
    textColor: style?.primary ? COLOR.text : COLOR.textMuted,
    bold: style?.primary,
})

// -----------------------------------------------------------------------------
// The quantity picker. Take / Give on a stack, and Craft on a recipe the pack
// covers more than once, open this small panel over the screen instead of firing
// straight away, so the player says how many before anything moves. Three ways
// in, all writing the same number: drag the slider, tap a chip (1 / Half / All),
// or type the digits. The amount is on the readout AND on the confirm button, so
// it is legible wherever the pointer is. Geometry and arithmetic live in
// quantity_picker.ts.
// -----------------------------------------------------------------------------
interface QuantityPickerProps {
    logic: ClientGameLogic
    // The screen this floats inside, whose Escape it borrows while it is up.
    modal?: Modal
    itemTitle: string
    prompt: QuantityPrompt
    panelW: number
    panelH: number
    // Where the amount starts. Half the stack unless said otherwise.
    initial?: number
    // How many items one unit of the amount makes -- a craft of planks makes two. Above one, the
    // picker says what the count comes to, so "4" is never read as four planks.
    per?: number
    onConfirm: Callback<number>
    onCancel: Callback<void>
}

// Every key the picker answers to (see onKey). Nothing else is taken off the player while it is up.
const PICKER_KEYS = /^([0-9]|Backspace|Delete|Enter|Escape|Esc|ArrowLeft|ArrowRight|ArrowUp|ArrowDown)$/

class QuantityPicker extends Container {
    private props: QuantityPickerProps
    private max: number
    private value: number
    // Digits typed since the picker opened. Empty means "not typing", and the readout shows `value`.
    // Kept as a string, not a number, because a half-typed "1" on the way to "12" must read as "1"
    // without the slider snapping to 1 and back.
    private buffer: string = ""
    private dragging: boolean = false
    private released: boolean = false

    private panel: Container = new Container()
    private slider: Graphics = new Graphics()
    private readout: Graphics = new Graphics()
    private trackHit: Graphics = new Graphics()
    // Sits over the whole panel for the duration of a drag so every move lands on ONE object, rather
    // than being swallowed by whatever the pointer happens to be over. Draws nothing.
    private capture: Graphics = new Graphics()
    private amountText: Text
    // "makes 8", on the title row. Only when one unit makes several.
    private yieldText?: Text
    private confirmButton: UiButton

    constructor(props: QuantityPickerProps) {
        super()
        this.props = props
        this.max = Math.max(2, Math.floor(props.prompt.max))
        // Half the stack by default: the middle is the one starting point that is a short drag from
        // every answer, and the whole stack is one click away on the All chip.
        this.value = props.initial === undefined ? halfOf(this.max) : clampQuantity(props.initial, this.max)

        this.buildScrim()
        this.buildPanel()
        this.addChild(this.panel)
        this.addChild(this.capture)

        this.capture.interactive = true
        this.capture.visible = false
        this.capture.hitArea = new Rectangle(0, 0, props.panelW, props.panelH)
        this.capture.on("pointermove", this.onDragMove)
        this.capture.on("pointerup", this.endDrag)
        this.capture.on("pointerupoutside", this.endDrag)

        this.attachKeys()
        this.sync()
    }

    // A dim wash over the panel: says "answer this first", and blocks the item slots underneath so a
    // stray hover can't reopen the menu the picker was launched from.
    private buildScrim = () => {
        const { panelW, panelH } = this.props
        const scrim = new Graphics()
        scrim.beginFill(COLOR.slotInset, 0.66)
        scrim.drawRect(0, 0, panelW, panelH)
        scrim.endFill()
        scrim.interactive = true
        this.addChild(scrim)
    }

    private buildPanel = () => {
        const { itemTitle, prompt, panelW, panelH } = this.props
        const origin = pickerOrigin(panelW, panelH)
        this.panel.x = origin.x
        this.panel.y = origin.y

        const chrome = new Graphics()
        drawPlate(chrome, 0, 0, PICKER_W, PICKER_H, { face: COLOR.panelFill, faceAlpha: 0.99, rim: COLOR.gold, rimAlpha: 0.9, c: 7 })
        this.panel.addChild(chrome)

        const title = text(`${prompt.verb} ${itemTitle}`, "caption", COLOR.title, true)
        title.x = PICKER_PAD
        title.y = TITLE_Y
        this.panel.addChild(title)

        if ((this.props.per ?? 1) > 1) {
            // Right-aligned against the margin in sync(), since its width changes with the amount.
            this.yieldText = text("", "compactCaption", COLOR.textMuted)
            this.yieldText.y = TITLE_Y + (title.height - this.yieldText.height) / 2
            this.panel.addChild(this.yieldText)
        }

        // Readout row: the boxed amount, with "/ max" alongside. Centered as one group, so the box
        // stays put no matter how many digits the number grows to.
        const ofMax = text(`/ ${this.max}`, "compactBody", COLOR.textMuted)
        const groupW = READOUT_W + READOUT_GAP + ofMax.width
        const boxX = Math.round((PICKER_W - groupW) / 2)
        this.readout.x = boxX
        this.readout.y = READOUT_Y
        this.readout.interactive = true
        this.readout.cursor = "text"
        this.readout.hitArea = new Rectangle(0, 0, READOUT_W, READOUT_H)
        // Clicking the box means "let me retype this": empty the buffer and wait for digits.
        this.readout.on("pointerdown", () => {
            this.buffer = ""
            this.sync()
        })
        this.panel.addChild(this.readout)

        this.amountText = text("", "title", COLOR.text, true)
        this.panel.addChild(this.amountText)

        ofMax.x = boxX + READOUT_W + READOUT_GAP
        ofMax.y = READOUT_Y + (READOUT_H - ofMax.height) / 2 + 1
        this.panel.addChild(ofMax)

        // Steppers pinned to the margins, slider between them.
        const minus = makeButton("-", STEP_SIZE, STEP_SIZE, () => this.step(-1))
        minus.x = PICKER_PAD
        minus.y = STEP_Y
        this.panel.addChild(minus)

        const plus = makeButton("+", STEP_SIZE, STEP_SIZE, () => this.step(1))
        plus.x = PICKER_W - PICKER_PAD - STEP_SIZE
        plus.y = STEP_Y
        this.panel.addChild(plus)

        this.panel.addChild(this.slider)

        this.trackHit.interactive = true
        this.trackHit.cursor = "pointer"
        // Taller than the track and wider by a handle radius at each end, so the ends are reachable
        // and the grab doesn't demand pixel accuracy.
        this.trackHit.hitArea = new Rectangle(
            TRACK_X - HANDLE_R,
            TRACK_CENTER_Y - 12,
            TRACK_W + HANDLE_R * 2,
            24,
        )
        this.trackHit.on("pointerdown", this.beginDrag)
        this.trackHit.on("pointerup", this.endDrag)
        this.trackHit.on("pointerupoutside", this.endDrag)
        this.panel.addChild(this.trackHit)

        const chips: { label: string; amount: () => number }[] = [
            { label: "1", amount: () => 1 },
            { label: "Half", amount: () => halfOf(this.max) },
            { label: "All", amount: () => this.max },
        ]
        chips.forEach((chip, i) => {
            const button = makeButton(chip.label, CHIP_W, CHIP_H, () => this.set(chip.amount()), {
            })
            button.x = CHIP_ROW_X + i * (CHIP_W + CHIP_GAP)
            button.y = CHIP_Y
            this.panel.addChild(button)
        })

        const cancel = makeButton("Cancel", CANCEL_W, ACTION_H, () => this.dismissWith(this.props.onCancel))
        cancel.x = PICKER_PAD
        cancel.y = ACTION_Y
        this.panel.addChild(cancel)

        this.confirmButton = makeButton("", CONFIRM_W, ACTION_H, this.confirm, { primary: true })
        this.confirmButton.x = CONFIRM_X
        this.confirmButton.y = ACTION_Y
        this.panel.addChild(this.confirmButton)
    }

    // ---- state ----------------------------------------------------------

    // The amount as it stands: the typed digits while typing, otherwise the slider's value. Undefined
    // only when the buffer has been emptied out -- a half-typed number with nothing to transfer yet.
    private committed = (): number | undefined =>
        this.buffer === "" ? this.value : bufferQuantity(this.buffer, this.max)

    private set = (amount: number) => {
        this.value = clampQuantity(amount, this.max)
        this.buffer = ""
        this.sync()
    }

    private step = (delta: number) => this.set((this.committed() ?? this.value) + delta)

    // Redraws only what changes with the amount (readout, handle, confirm label). The chrome, labels
    // and buttons are built once -- a full rebuild on every drag frame would churn a Text texture per
    // pixel of travel.
    private sync = () => {
        const amount = this.committed()
        const typing = this.buffer !== ""

        this.amountText.text = typing ? this.buffer : `${this.value}`
        this.amountText.x = this.readout.x + (READOUT_W - this.amountText.width) / 2
        this.amountText.y = READOUT_Y + (READOUT_H - this.amountText.height) / 2

        this.readout.clear()
        drawSocket(this.readout, 0, 0, READOUT_W, READOUT_H, COLOR.slotEmpty, 4)
        if (typing) {
            this.readout.lineStyle({ width: 1.5, color: COLOR.slotHover, alpha: 1, join: LINE_JOIN.MITER })
            chamfer(this.readout, 0.75, 0.75, READOUT_W - 1.5, READOUT_H - 1.5, 4)
            this.readout.lineStyle(0)
        }
        if (typing || amount === undefined) {
            // A plain caret rather than a blinking one: a blink needs a timer per open picker, and the
            // gold box edge already says the field has the keyboard.
            this.readout.beginFill(COLOR.gold, 0.9)
            this.readout.drawRect(
                this.amountText.x - this.readout.x + this.amountText.width + 2,
                6,
                2,
                READOUT_H - 12,
            )
            this.readout.endFill()
        }

        this.drawSlider()

        if (this.yieldText) {
            this.yieldText.text = amount === undefined ? "" : `makes ${amount * (this.props.per ?? 1)}`
            this.yieldText.x = PICKER_W - PICKER_PAD - this.yieldText.width
        }

        const { verb } = this.props.prompt
        this.confirmButton.setLabel(amount === undefined ? verb : `${verb} ${amount}`)
        this.confirmButton.setEnabled(amount !== undefined)
    }

    private drawSlider = () => {
        const g = this.slider
        const shown = this.committed() ?? this.value
        const filled = offsetForQuantity(shown, this.max)
        const top = TRACK_CENTER_Y - TRACK_H / 2
        g.clear()
        g.beginFill(COLOR.slotInset, 1)
        g.drawRoundedRect(TRACK_X, top, TRACK_W, TRACK_H, TRACK_H / 2)
        g.endFill()
        g.lineStyle(1, RIM_LIGHT, 0.12).moveTo(TRACK_X + TRACK_H / 2, top + TRACK_H + 0.5)
            .lineTo(TRACK_X + TRACK_W - TRACK_H / 2, top + TRACK_H + 0.5)
        g.lineStyle(0)
        g.beginFill(COLOR.gold, 0.9)
        g.drawRoundedRect(TRACK_X, top, Math.max(TRACK_H, filled), TRACK_H, TRACK_H / 2)
        g.endFill()
        // A bronze boss with a gold heart while it is held -- the wheel's hub in miniature.
        g.beginFill(0x000000, 0.4).drawCircle(TRACK_X + filled, TRACK_CENTER_Y + 1.5, HANDLE_R).endFill()
        g.lineStyle(1.5, RIM, 1)
        g.beginFill(this.dragging ? COLOR.slotHover : COLOR.text, 1)
        g.drawCircle(TRACK_X + filled, TRACK_CENTER_Y, HANDLE_R)
        g.endFill()
        g.lineStyle(0)
    }

    // ---- slider drag ----------------------------------------------------

    private beginDrag = (event: InteractionEvent) => {
        this.dragging = true
        this.capture.visible = true
        this.onDragMove(event)
    }

    private onDragMove = (event: InteractionEvent) => {
        if (!this.dragging) {
            return
        }
        const local = event.data.getLocalPosition(this.panel)
        this.set(quantityAtOffset(local.x - TRACK_X, this.max))
    }

    private endDrag = () => {
        if (!this.dragging) {
            return
        }
        this.dragging = false
        this.capture.visible = false
        this.drawSlider()
    }

    // ---- keyboard -------------------------------------------------------

    // The picker takes the keys it uses while it is up: digits would otherwise hit the hotbar, the
    // arrows would walk the player, Enter would open chat, and Escape would tear the whole screen down
    // rather than backing out of the picker. Only those -- the world stays live behind the pack, so
    // WASD still walks and everything else still plays.
    private claimsKey = (key: string): boolean => PICKER_KEYS.test(key)

    private attachKeys = () => {
        this.props.logic.playerController.claimKeys(this.claimsKey)
        this.props.modal?.suspendEscape(true)
        this.props.logic.clientRenderer.keyboard().addListener(this.onKey)
    }

    private detachKeys = () => {
        this.props.logic.clientRenderer.keyboard().removeListener(this.onKey)
        this.props.modal?.suspendEscape(false)
        this.props.logic.playerController.releaseKeys(this.claimsKey)
    }

    private onKey = (key: string, down: boolean) => {
        if (!down || this.released) {
            return
        }
        if (/^[0-9]$/.test(key)) {
            this.buffer = applyDigit(this.buffer, key, this.max)
            const typed = bufferQuantity(this.buffer, this.max)
            if (typed !== undefined) {
                this.value = typed
            }
            this.sync()
            return
        }
        if (key === "Backspace" || key === "Delete") {
            this.buffer = applyBackspace(this.buffer === "" ? `${this.value}` : this.buffer)
            const typed = bufferQuantity(this.buffer, this.max)
            if (typed !== undefined) {
                this.value = typed
            }
            this.sync()
            return
        }
        if (key === "Enter") {
            this.confirm()
            return
        }
        if (key === "Escape" || key === "Esc") {
            this.dismissWith(this.props.onCancel)
            return
        }
        if (key === "ArrowLeft" || key === "ArrowDown") {
            this.step(key === "ArrowDown" ? -10 : -1)
            return
        }
        if (key === "ArrowRight" || key === "ArrowUp") {
            this.step(key === "ArrowUp" ? 10 : 1)
        }
    }

    // ---- teardown -------------------------------------------------------

    private confirm = () => {
        const amount = this.committed()
        if (amount === undefined) {
            return
        }
        this.dismissWith(() => this.props.onConfirm(amount))
    }

    private dismissWith = (then: Callback<void>) => {
        this.dismiss()
        then()
    }

    // Idempotent, and safe to call from anywhere the screen can be torn down under us (a re-render
    // after a transfer, the close button, the room changing) -- whatever happens, the keyboard has to
    // go back to the player.
    dismiss = () => {
        if (this.released) {
            return
        }
        this.released = true
        this.endDrag()
        this.detachKeys()
        this.parent?.removeChild(this)
    }
}

export class ModernInventoryDialog extends DynamicDialog {
    private modernItemMap: Map<string, Graphics> = new Map()
    private itemDrag?: ItemSlotDrag

    // The quantity picker floats over the panel, so it lives in its own layer above the slots and the
    // hover menu. Only one can be open, and it has to be dismissed whenever the panel is rebuilt or
    // closed -- it holds the keyboard while it is up.
    private pickerLayer?: Container
    private quantityPicker?: QuantityPicker
    // A local redraw asked for while the picker was up, run once it closes (see refreshCrafting).
    private refreshDeferred = false

    // Panel dimensions and stage offset. Mobile screens use centered columns; desktop screens
    // use the narrow dock. Recomputed per message because crafting reuses the inventory modal.
    private panelW: number
    private panelH: number
    private dockX: number
    private horizontalPanel = false

    // Where the ember tutorial's clues are drawn. It hangs off the panel WRAPPER rather than the body
    // so that assemble()'s measurement of the body -- which is what sizes the panel background -- is
    // not thrown off by a box that deliberately sticks out to the left of the panel. Rebuilt with the
    // rest of the content, so a stale anchor can never point into a discarded tree.
    private clueLayer?: Container
    private panelClueAnchors = new Map<EmberClueType, PanelClueAnchor>()
    private panelAssembled = false
    // Where the hover pill is drawn. Cleared and redrawn per hover rather than kept per slot: only one
    // can be up at a time, and a rebuilt panel throws the whole tree away anyway.
    private pillLayer?: Container
    // How far down the crafting grid we are.
    //
    // Kept on the DIALOG rather than in the drawn tree, because the panel is rebuilt from scratch on
    // every server message -- and the server sends one after every craft. An offset stored anywhere
    // in the content would reset to the top each time a player made something, which is precisely
    // when they are most likely to be half way down a list.
    private craftingScrollY = 0
    // Set while a press over the grid has turned into a scroll, so the tile underneath
    // it does not also fire. Pixi delivers the button's click after the drag ends, so this is checked
    // rather than the event being cancelled.
    private recipeDragMoved = false

    // The live scrolling list: the container that moves, how far it may move, and the scrollbar thumb
    // that tracks it. Held so a gesture can shift the list directly -- rebuilding the panel mid-drag
    // destroys the display object handling that very drag, which is how the first attempt managed to
    // scroll nowhere at all.
    private recipeStrip?: Container
    private recipeMaxScroll = 0
    private scrollThumb?: Graphics
    private scrollTravel = 0
    // pixi does not deliver `wheel`, so it comes off the DOM canvas and has to be taken off again.
    private wheelListener?: (event: WheelEvent) => void
    private resizeListener?: () => void

    // The crafting screen's own state. All of it lives on the DIALOG for the same reason the scroll
    // offset does -- the panel is rebuilt on every server message, and the server sends one after
    // every craft, so anything held in the drawn tree would reset itself under the player.
    //
    // The recipe on the bench (by row name), and the kind last picked for each folded family.
    private craftingSelected?: string
    private craftingPicks: Record<string, string> = {}
    // The search: open (in the rail's place) or not, and what has been typed.
    private craftingSearchOpen = false
    private craftingQuery = ""
    private focusSearchOnShow = false
    // A family a reveal or a jump asked to bring into view on the next build.
    private craftingRevealFamily?: string
    // What could be made in the last list, so this one can light what is new.
    private craftingEnabled?: Set<string>
    // The list the in-flight count belongs to, and how many crafts have been sent since it arrived.
    private craftingMessage?: ScreenMessage
    private craftsInFlight = 0
    // A held Craft button: what it is making and how long until the next.
    private craftHold?: { key: string; delay: number }
    private craftHoldTimer?: ReturnType<typeof setTimeout>
    // This build's geometry and the nodes the motion and the rail reach for between rebuilds.
    private craftGrid?: { layout: GridLayout; rect: Rect; viewH: number; groups: CatalogGroup[] }
    private railTabs: { key: string; label: Text; bar: Graphics }[] = []
    private pinnedHeaders: { line: GridLine; band: Graphics }[] = []
    private craftTiles = new Map<string, PlacedTile[]>()
    private craftIngredients = new Map<string, { x: number; y: number; count: Text }>()
    private craftWell?: { x: number; y: number; size: number; sprite?: BasicSprite }
    private craftButtonRect?: { x: number; y: number; w: number; h: number }
    private craftButtonGlow?: Graphics
    private packTabCenter?: { x: number; y: number }
    private packTabRect?: { x: number; y: number; w: number; h: number }
    // The motion: a layer that survives rebuilds, the ticker that drives it, and what is moving.
    private craftFx?: Container
    private craftFxTick?: () => void
    private craftFlights: CraftFlight[] = []
    private craftSparks: CraftSpark[] = []
    private craftPops = new Map<string, number>()
    private craftGlows = new Map<string, { start: number; duration: number }>()
    private craftBadge?: { count: number; until: number }
    private craftBadgeDirty = false
    private craftScrollTween?: { from: number; to: number; start: number }
    // The search box takes the keyboard off the player while it is focused, so it must always be
    // possible to hand it back -- including on a teardown the box never saw coming.
    private craftingSearchFocused = false
    // The real <input> over the drawn box, and where it was last put. Parented to the PAGE, not to
    // this panel -- the panel is rebuilt constantly and a node inside it would be torn out and
    // re-appended mid-word.
    private searchField?: SearchField
    private searchRect?: OverlayRect
    // The drawn frame the field has to sit in, kept so it can be measured once the panel is actually
    // on the stage rather than while it is still being built.
    private searchBox?: { box: Graphics; w: number; h: number }

    // The panel's entrance (see beginEntrance): the wrapper it is applied to, that wrapper's resting
    // place, the screen last drawn (so a turn to another screen can be told from a rebuild), and the
    // motion in flight.
    private panelWrapper?: Graphics
    private panelOrigin = { x: 0, y: 0 }
    private screenKind?: string
    private entrance?: { elapsed: number; ms: number; fromAlpha: number; slide: number }
    private entranceTick?: () => void
    // The profile card (drawProfile) and its live HP: the hit effect is held here rather than on the
    // card, because the card is rebuilt with the panel and a hit should finish playing across that.
    private profileCard?: PackProfileCard
    private profileEntityId?: EntityId
    private profilePayloadHp?: { hp: number; max: number }
    private profileHp?: number
    private profileFx = new RosterDamageFx()
    private profileTick?: () => void
    // Hover + 1-6: the ready bar as last drawn, and the item under the pointer while the digits are held.
    private readiedForKeys: ItemDetailMeta[] = []
    private keyHover?: { item: ItemDetailMeta; section: ItemDropTarget }
    private readyKeysAttached = false
    private readyKeysClaimed = false

    // Redraw the panel in place, for a change the client made on its own (a scroll) rather than one
    // the server told it about. rebuildSurface is FullScreenModal's own "repaint when you decide to"
    // hook, and does the Text teardown that a naive removeChildren would leak.
    //
    // Held while the quantity picker is up: a rebuild throws away the tree the picker floats in, and
    // a redraw the client asked for itself (a reveal glow finishing, the search box letting go) is
    // not worth losing a half-chosen amount over. It runs when the picker closes.
    private refreshCrafting = () => {
        if (this.quantityPicker) {
            this.refreshDeferred = true
            return
        }
        this.rebuildSurface()
    }

    private dockOrigin = () => {
        const viewport = stageViewport()
        if (this.horizontalPanel) {
            // The fullscreen dialog adds 10px of content padding in both axes.
            const centeredY = (viewport.height - this.panelH * MOBILE_PANEL_SCALE) / 2 - 10
            const belowChipsY = this.guiManager.emberInventoryChip?.visible
                ? this.guiManager.itemPanelDefaultAnchor(this.panelW * MOBILE_PANEL_SCALE).y
                    - viewport.top - 10 - PANEL_INSET * MOBILE_PANEL_SCALE
                : centeredY
            return {
                x: (viewport.width - this.panelW * MOBILE_PANEL_SCALE) / 2 - 10,
                y: Math.max(centeredY, belowChipsY),
            }
        }
        if (!isMobile() && this.isInventoryOrCrafting()) {
            const at = this.guiManager.itemPanelDefaultAnchor((this.panelW - PANEL_INSET * 2) * MOBILE_PANEL_SCALE)
            return {
                x: at.x - viewport.left - 10 - PANEL_INSET * MOBILE_PANEL_SCALE,
                y: at.y - viewport.top - 10 - PANEL_INSET * MOBILE_PANEL_SCALE,
            }
        }
        return { x: this.dockX - viewport.left, y: -viewport.top }
    }

    constructor(guiManager: GuiManager, logic: ClientGameLogic, message: ScreenMessage, onClose?: Callback<void>) {
        // Mobile item screens are centered; desktop screens dock to the right. The close button
        // follows the panel's top-right corner within the transparent fullscreen dialog. Every item
        // screen has one: the Items chip toggles the pack too, but it is not always on screen (a
        // chapter can hold the chip row back, and it goes while a held line is read), and a panel
        // with no visible way out is a trap.
        super(guiManager, logic, message, onClose, {
            backgroundAlpha: 0,
            // Inventory and crafting seat the disc at the end of their header row. Hung off the corner, as
            // trade's still is, it sat on the chip row the docked panel hangs under (the MAP or Items
            // chip), which is why those two once hid it altogether.
            closeAnchor: () => this.isInventoryOrCrafting()
                ? {
                    x: this.dockOrigin().x + (this.panelW - HEADER_RIGHT_MARGIN - closeControlRadius()) * MOBILE_PANEL_SCALE,
                    y: this.dockOrigin().y + (HEADER_BUTTON_Y + HEADER_BUTTON_H / 2) * MOBILE_PANEL_SCALE,
                }
                : {
                    x: this.dockOrigin().x + (this.panelW - PANEL_INSET) * MOBILE_PANEL_SCALE,
                    y: this.dockOrigin().y + PANEL_INSET * MOBILE_PANEL_SCALE,
                },
            closeButtonScale: MOBILE_PANEL_SCALE,
            // The disc that hangs off this panel's corner is in the panel's timber, not the slate
            // every other dialog's still wears.
            closeTheme: "ember",
            dragHandle: () => ({
                ...this.dockOrigin(),
                width: this.panelW * MOBILE_PANEL_SCALE, height: this.panelH * MOBILE_PANEL_SCALE,
            }),
            dragBounds: () => {
                const viewport = stageViewport()
                const origin = this.dockOrigin()
                const left = viewport.left - origin.x - 10 + 6
                const top = viewport.top - origin.y + 6
                return {
                    left,
                    right: Math.max(left, viewport.right - origin.x - this.panelW * MOBILE_PANEL_SCALE - 10 - 24),
                    top,
                    bottom: Math.max(top, viewport.bottom - origin.y - this.panelH * MOBILE_PANEL_SCALE - 10 - 6),
                }
            },
            onDrag: () => this.positionSearchField(),
            // The item screens leave the world live -- walking, fighting, clicking things -- while
            // any other server dialog this modal shows still closes it off. See leavesWorldLive.
            blocksWorld: () => !this.leavesWorldLive(),
        })
        this.panelW = DOCK_W
        this.panelH = DOCK_H / MOBILE_PANEL_SCALE
        this.dockX = SCALED_DOCK_X

        // Closing the screen out from under an open picker (close button, Escape, a room change) has
        // to hand the keyboard back. DynamicDialog has already installed onClose by the time this
        // runs, so wrap whatever it set.
        const inherited = this.onClose
        this.onClose = (clicked: boolean) => {
            this.itemDrag?.cancel()
            this.dismissQuantityPicker()
            // The wheel listener lives on the DOM canvas, which outlives this panel by a long way --
            // left behind it would scroll a recipe list that is no longer on screen.
            this.detachWheel()
            // ...and the search box may be holding the keyboard. Closing the panel out from under it
            // has to give the player their movement keys back -- without asking a panel that is going
            // away to draw itself again.
            this.applySearchFocus(false)
            // Anything still moving when the panel closes would keep a ticker listener alive against a
            // tree that no longer exists, and a held Craft button would go on crafting.
            this.stopCraftFx()
            this.stopCraftHold()
            this.stopEntrance()
            this.stopProfileTick()
            this.profileCard = undefined
            this.hoverForKeys(undefined)
            this.detachReadyKeys()
            // The search field is parented to the page, so nothing else will ever take it off. A node
            // left behind is the ghost input that has bitten the login card before.
            this.searchField?.dispose()
            this.searchField = undefined
            this.searchBox = undefined
            this.detachResize()
            // The clued controls go off screen with the panel. The clues stay ARMED (reopening the pack
            // puts them back) but nothing is left pointing at a control that isn't there.
            this.retractClues()
            if (inherited) {
                inherited(clicked)
            }
        }
    }

    createContent = (): Graphics => {
        this.itemDrag?.cancel()
        this.itemDrag = undefined
        // The tree these pointed into is about to go: the card is redrawn below if this screen has one,
        // and a hovered slot has to be hovered again before its number keys mean anything.
        this.profileCard = undefined
        this.hoverForKeys(undefined)
        // Crafting reuses this modal, so reset the dimensions before choosing each screen's layout.
        this.horizontalPanel = false
        this.panelW = DOCK_W
        this.panelH = DOCK_H / MOBILE_PANEL_SCALE
        this.dockX = SCALED_DOCK_X
        this.modernItemMap = new Map()
        // Every rebuild (including the one that follows a transfer) throws the old tree away, the
        // picker with it. Retire it properly first so it gives the keyboard back.
        this.dismissQuantityPicker()
        this.pickerLayer = undefined
        // Same for the tutorial clues: the tree they were drawn into is about to be discarded, so drop
        // every anchor and let this pass re-report whichever controls it actually draws.
        this.retractClues()
        this.clueLayer = undefined
        this.panelClueAnchors.clear()
        this.panelAssembled = false
        // The field is parented to the PAGE, so switching this panel to a screen that has no search
        // box would otherwise leave it sitting over the backpack grid.
        //
        // Only on the way to ANOTHER screen, though. `display: none` on a focused input blurs it, and
        // this runs on every rebuild -- including the one caused by the keystroke that was just typed
        // into it. Hiding unconditionally cost the field its focus after a single letter.
        if (!isCraftingMessage(this.message)) {
            this.searchField?.hide()
        }

        // Crafting and trading share the mobile inventory's centered, horizontal presentation.
        if (isMobile() && (isTradeMessage(this.message) || isCraftingMessage(this.message))) {
            this.horizontalPanel = true
            this.panelW = MOBILE_PANEL_W + (isTradeMessage(this.message) ? 32 : 0)
        }

        if (isTradeMessage(this.message)) {
            return this.createTradeContent()
        }

        // The crafting screen reuses this same modal (it opens from the inventory's Craft button),
        // so route it to the modern crafting layout rather than the generic fallback.
        if (isCraftingMessage(this.message)) {
            return this.createCraftingContent()
        }

        const parsed = this.parse(this.message)

        // Only the player's own inventory renders in the modern layout. Anything else that reuses
        // this modal (or a mis-fired open-intent flag) falls back to the classic generic renderer so
        // it looks and behaves exactly as before. DynamicDialog.createContent is an instance arrow
        // field on the base, so `super.createContent` doesn't resolve — call the static form directly.
        if (!this.isSelfInventory(parsed)) {
            return DynamicDialog.createContent(this.logic, this.message, this.width() || PANEL_W, this.grids)
        }

        this.horizontalPanel = isMobile()
        if (this.horizontalPanel) {
            this.panelW = MOBILE_PANEL_W
        }

        return this.assemble((body, detailCard) => {
            this.drawTitleBar(body, parsed)
            const sockets = this.profileSockets(parsed)
            this.drawProfile(body, parsed, sockets, detailCard)
            this.drawReadied(body, parsed, detailCard)
            this.drawBackpack(body, parsed, detailCard, sockets)
        })
    }

    // Builds a docked panel that sizes its height to the content. The body is drawn first, then the
    // rounded panel background is sized to fit behind it — so no screen has awkward empty space
    // below its content. The whole thing is offset to the right dock position.
    //
    // `fixedHeight` opts out of the measurement, and is a THUNK because the caller only learns the
    // answer while drawing (the tabs decide where the list starts). The crafting screen needs it: its
    // recipe list is a
    // SCROLLING window, and a masked child still reports its full height to getLocalBounds, so a long
    // group would stretch the panel off the bottom of the screen even though the mask hid the
    // overflow. craftingLayout already knows exactly how tall the panel should be, so it says.
    private assemble = (
        drawBody: (body: Graphics, card: ModernDetailCard) => void,
        fixedHeight?: () => number | undefined,
        drawOverlay?: (body: Graphics) => void,
    ): Graphics => {
        const wrapper = new Graphics()
        const bg = new Graphics()
        const body = new Graphics()
        wrapper.addChild(bg)
        wrapper.addChild(body)

        // Created before the body is drawn (drawBody reports its clued controls as it goes) but
        // parented after it, so it draws over the panel and is never part of the body's measurement.
        // It shares the body's coordinate space: both sit at the wrapper's origin.
        const clueLayer = new Container()
        this.clueLayer = clueLayer

        const detailCard = this.clueOnDetailCard(new ModernDetailCard())
        this.itemDrag = new ItemSlotDrag(
            getRendererApp().renderer.plugins.interaction, body,
            (item, size) => {
                const ghost = new Container()
                const frame = new Graphics()
                // A larger, opaque tile keeps even tiny item art visible over the world and slots.
                frame.beginFill(0x000000, 0.55)
                chamfer(frame, -2, 0, size + 8, size + 8, 5)
                frame.endFill()
                frame.lineStyle({ width: 2, color: COLOR.slotHover, alpha: 1, join: LINE_JOIN.MITER })
                frame.beginFill(COLOR.slotFill, 1)
                chamfer(frame, -4, -4, size + 8, size + 8, 5)
                frame.endFill()
                frame.lineStyle(1, RIM_LIGHT, 0.25)
                frame.moveTo(1, -1.5).lineTo(size - 1, -1.5)
                ghost.addChild(frame)
                const icon = new BasicSprite({ name: item.spriteId, scale: (0.78 * size) / SLOT,
                    color: 0xffffff, animated: false, rotating: false })
                ghost.addChild(icon)
                fitItemIcon(icon, 0, 0, size)
                // The tile carries its gauge while it is in the air. Dropping a worn weapon into a
                // chest is a decision made ABOUT its wear, and the pack it came from is covered by
                // the thing being dragged over it.
                const wear = WearBar.forSlot(item.condition, size)
                if (wear) {
                    ghost.addChild(wear)
                }
                return ghost
            },
            this.selectItemOption,
            () => { detailCard.hideImmediately(); this.hideNamePill() },
            point => {
                const at = body.toLocal(point)
                return getRendererApp().screen.contains(point.x, point.y)
                    && !new Rectangle(0, 0, this.panelW, this.panelH).contains(at.x, at.y)
            },
        )
        drawBody(body, detailCard)
        // Register before world fallback: releasing over the HUD must ready the item, not drop it.
        if (!isMobile()) this.guiManager.equipped?.registerInventoryDropTarget(this.itemDrag)
        // Shared item menus always render above the slots on every screen.
        body.addChild(detailCard)
        drawOverlay?.(body)

        // Measure the visible content (the hidden detail card is excluded) and size the panel to it,
        // unless the caller already knows the answer.
        const b = body.getLocalBounds()
        const h = fixedHeight?.() ?? (b.height > 0 ? b.y + b.height + PANEL_BOTTOM_PAD : this.panelH)
        this.panelH = h
        this.drawPanelBg(bg, h)

        // Mounted last, and after the measurement, so an open picker floats over everything without
        // stretching the panel it is floating over.
        this.pickerLayer = new Container()
        body.addChild(this.pickerLayer)

        // The hover pill, over the panel like the clues but under them: a clue is a standing
        // instruction, a pill is a passing label, and the instruction wins if they ever overlap.
        this.pillLayer = new Container()
        wrapper.addChild(this.pillLayer)

        wrapper.addChild(clueLayer)

        // Position after measuring so mobile inventory centers using its actual height.
        const origin = this.dockOrigin()
        wrapper.position.set(origin.x, origin.y)
        wrapper.scale.set(MOBILE_PANEL_SCALE)
        this.panelWrapper = wrapper
        this.panelOrigin = { x: origin.x, y: origin.y }
        // Opening the panel slides it in off its dock; turning to another of its screens is a softer
        // version of the same. The many rebuilds in between (every server message, keystroke and
        // scroll) are neither, and must not move anything.
        const kind = isTradeMessage(this.message) ? "trade" : isCraftingMessage(this.message) ? "crafting" : "inventory"
        if (this.screenKind === undefined) {
            this.beginEntrance(ENTRANCE_MS, 0, ENTRANCE_SLIDE)
        } else if (kind !== this.screenKind) {
            this.beginEntrance(TURN_MS, TURN_FROM_ALPHA, TURN_SLIDE)
        }
        this.screenKind = kind
        this.applyEntrance()
        this.panelAssembled = true
        this.panelClueAnchors.forEach((anchor, type) => this.registerPanelClue(type, anchor))
        return wrapper
    }

    // ---- the entrance ------------------------------------------------------
    //
    // Driven off the renderer's ticker by elapsed frame time, and applied to whichever wrapper is
    // current, because a rebuild can replace the wrapper mid-slide (the server answers an open with a
    // second message often enough). With no ticker -- tests, a renderer not up yet -- the panel simply
    // appears, which is what it always did.

    private beginEntrance = (ms: number, fromAlpha: number, slide: number) => {
        const ticker = this.ticker()
        if (!ticker) {
            return
        }
        this.entrance = { elapsed: 0, ms, fromAlpha, slide }
        if (!this.entranceTick) {
            this.entranceTick = () => {
                if (!this.entrance) {
                    return
                }
                this.entrance.elapsed += ticker.deltaMS
                this.applyEntrance()
                if (!this.entrance) {
                    this.stopEntrance()
                    // The DOM search field was placed over the box while it was still sliding.
                    this.positionSearchField()
                }
            }
            ticker.add(this.entranceTick)
        }
    }

    private applyEntrance = () => {
        const wrapper = this.panelWrapper
        if (!wrapper || wrapper.destroyed) {
            return
        }
        const motion = this.entrance
        const t = motion ? Math.min(1, motion.elapsed / motion.ms) : 1
        const eased = 1 - Math.pow(1 - t, 3)
        const offset = motion ? motion.slide * (1 - eased) : 0
        wrapper.alpha = motion ? motion.fromAlpha + (1 - motion.fromAlpha) * eased : 1
        // Off its own dock: a right-docked panel comes in from the right, a centred one from below.
        wrapper.position.set(
            this.panelOrigin.x + (this.horizontalPanel ? 0 : offset),
            this.panelOrigin.y + (this.horizontalPanel ? offset : 0),
        )
        if (t >= 1) {
            this.entrance = undefined
        }
    }

    private stopEntrance = () => {
        if (this.entranceTick) {
            this.ticker()?.remove(this.entranceTick)
        }
        this.entranceTick = undefined
        this.entrance = undefined
    }

    // ---- the hover name pill -----------------------------------------------

    /**
     * A small rounded label with the item's name, centred over the slot the pointer is on.
     *
     * Deliberately not a DyanmicTextContainer: this is redrawn on every slot the pointer crosses, and
     * a pill wants to be cheap to make and throw away.
     */
    private showNamePill = (title: string, slotX: number, slotY: number, size: number) => {
        const layer = this.pillLayer
        if (!layer || !title) {
            return
        }
        layer.removeChildren()

        const label = text(title, "compactCaption", PILL_TEXT)
        const w = Math.ceil(label.width) + PILL_PAD_X * 2
        const h = Math.ceil(label.height) + PILL_PAD_Y * 2

        const pill = new Graphics()
        drawPlate(pill, 0, 0, w, h, { face: PILL_FILL, rim: PILL_BORDER, rimAlpha: 0.9, c: 3 })

        label.x = PILL_PAD_X
        label.y = PILL_PAD_Y
        pill.addChild(label)

        const { x, y } = namePillLayout({ slotX, slotY, slotSize: size, pillW: w, pillH: h, panelW: this.panelW })
        pill.x = x
        pill.y = y

        layer.addChild(pill)
    }

    private hideNamePill = () => {
        this.pillLayer?.removeChildren()
    }

    // ---- ember tutorial clues ----------------------------------------------

    // "I drew this clued control, here." The tutorial layer decides whether anything comes of it.
    private clueAt = (tutorialType: EmberClueType, x: number, y: number, w: number, h: number, reveal?: PanelClueAnchor["reveal"],
        extra?: Pick<PanelClueAnchor, "lines" | "placement">) => {
        if (!this.clueLayer) {
            return
        }
        const anchor: PanelClueAnchor = { x, y, w, h, reveal, ...extra }
        this.panelClueAnchors.set(tutorialType, anchor)
        if (this.panelAssembled) this.registerPanelClue(tutorialType, anchor)
        const clues = this.guiManager.tutorialClues()
        // Drawing this control retires the one before it (see clueSatisfiedByDrawing). Order within a
        // rebuild does not matter: disarming wins, because registerAnchor on a disarmed clue is a no-op.
        const previous = clueSatisfiedByDrawing(tutorialType)
        if (previous) {
            clues.satisfyClue(previous)
        }
    }

    private registerPanelClue = (type: EmberClueType, anchor: PanelClueAnchor) => {
        const viewport = stageViewport()
        const safe = getUiMetrics().safeViewport
        const origin = this.dockOrigin()
        const x = viewport.left + 10 + origin.x
        const y = viewport.top + 10 + origin.y
        this.guiManager.tutorialClues().registerClueAnchor(type, {
            host: this.clueLayer,
            ...anchor,
            boxMinX: (safe.left - x + 8) / MOBILE_PANEL_SCALE,
            boxMaxX: (safe.right - x - 8) / MOBILE_PANEL_SCALE,
            boxMinY: (safe.top - y + 8) / MOBILE_PANEL_SCALE,
            boxMaxY: (safe.bottom - y - 8) / MOBILE_PANEL_SCALE,
            placement: anchor.placement ?? (isMobile()
                ? (type === "ember-crafting" || type === "ember-close" ? "below" : "above")
                : "left"),
            round: type === "ember-close",
            // The board dims and the control lights up: a note in the panel's own timber, laid over
            // the panel, otherwise reads as part of it.
            spotlight: {
                x: PANEL_INSET,
                y: PANEL_INSET,
                w: this.panelW - PANEL_INSET * 2,
                h: this.panelH - PANEL_INSET * 2,
                chamfer: PANEL_CHAMFER,
                // The close disc (the modal's) and the search box (a DOM field) sit over the panel, out
                // of the scrim's reach: darkened with it, unless the disc is what is being taught.
                onDim: brightness => {
                    const close = type === "ember-close" ? undefined : this.modal?.closeControl?.()
                    if (close && !close.destroyed) {
                        const v = Math.round(255 * brightness)
                        close.tint = (v << 16) | (v << 8) | v
                    }
                    this.searchField?.setBrightness(brightness)
                },
            },
        })
    }

    private retractClues = () => {
        const clues = this.guiManager.tutorialClues()
        PANEL_CLUES.forEach(tutorialType => clues.clearClueAnchor(tutorialType))
    }

    // The fifth clue lives on the hover menu, which only exists once the player has picked the item --
    // so the card reports its buttons as it lays them out, and this picks the campfire's "Ready" out of
    // them. Picking any other item (or the menu closing) takes the pointer off.
    private clueOnDetailCard = (card: ModernDetailCard) => {
        const clues = this.guiManager.tutorialClues()
        card.optionAnchors = (item, anchors) => {
            const title = (item.title || "").toLowerCase()
            for (const [itemTitle, type] of [[CLUED_ITEM, "ember-use"], ["wood", "ember-wood-use"], ["wall-wood", "ember-wall-use"], ["roof-wood", "ember-roof-use"]] as const) {
                const ready = title === itemTitle ? anchors.find(a => READY_OPTION.test(a.option.title || "")) : undefined
                if (ready) {
                    this.clueAt(type, ready.rect.x, ready.rect.y, ready.rect.width, ready.rect.height)
                } else {
                    clues.clearClueAnchor(type)
                }
            }
        }
        card.onHidden = () => {
            clues.clearClueAnchor("ember-use")
            clues.clearClueAnchor("ember-wood-use")
            clues.clearClueAnchor("ember-wall-use")
            clues.clearClueAnchor("ember-roof-use")
        }
        return card
    }

    // ---- quantity picker ----------------------------------------------------

    // A Take/Give on a stack asks how many first. Everything else fires on the click, as before.
    private openQuantityPicker = (item: ItemDetailMeta, option: ItemDetailOption) => {
        const prompt = option.quantityPrompt
        if (!prompt) {
            return false
        }
        return this.showQuantityPicker(item.title, prompt, amount => this.emitWithQuantity(option.onClickContext, amount))
    }

    // The amount rides beside the event's sealed transaction -- it was chosen after the event was
    // minted, so it cannot be inside it. The server clamps it.
    private emitWithQuantity = (event: any, amount: number) => {
        this.logic.playerController.client.emitPlayerEvent({
            ...event,
            context: { ...event?.context, transferQuantity: amount },
        })
    }

    private showQuantityPicker = (
        itemTitle: string,
        prompt: QuantityPrompt,
        onConfirm: Callback<number>,
        extra?: { initial?: number; per?: number },
    ): boolean => {
        if (!this.pickerLayer) {
            return false
        }
        this.dismissQuantityPicker()
        const picker = new QuantityPicker({
            logic: this.logic,
            modal: this.modal,
            itemTitle,
            prompt,
            panelW: this.panelW,
            panelH: this.panelH,
            ...extra,
            onConfirm: amount => {
                this.pickerClosed()
                onConfirm(amount)
            },
            onCancel: this.pickerClosed,
        })
        this.quantityPicker = picker
        this.pickerLayer.addChild(picker)
        // A tutorial clue bracketing the button that opened this would sit on top of the picker,
        // pointing at a control the scrim has just put out of reach.
        if (this.clueLayer) {
            this.clueLayer.visible = false
        }
        return true
    }

    // The picker answered itself (confirm or cancel), as opposed to being torn down by a rebuild.
    private pickerClosed = () => {
        this.quantityPicker = undefined
        if (this.clueLayer) {
            this.clueLayer.visible = true
        }
        if (this.refreshDeferred) {
            this.refreshDeferred = false
            this.refreshCrafting()
        }
    }

    // Menus and drops share quantity handling and event submission across all item screens.
    // A drag let go of over the world sends where, beside the sealed option, the way a quantity rides.
    private selectItemOption = (item: ItemDetailMeta, option: ItemDetailOption, at?: Point) => {
        if (this.openQuantityPicker(item, option)) return
        const dropAt = at && this.guiManager.worldPointAt(at)
        const event = option.onClickContext
        this.logic.playerController.client.emitPlayerEvent(dropAt ? { ...event, context: { ...event?.context, dropAt } } : { ...event })
    }

    private dismissQuantityPicker = () => {
        const picker = this.quantityPicker
        this.quantityPicker = undefined
        // Only ever called on the way into a rebuild or a close, either of which covers a redraw
        // that was waiting on the picker.
        this.refreshDeferred = false
        picker?.dismiss()
    }

    pulseGridItem = (itemId: EntityId) => {
        // In modern mode there's one slot per item; in classic-fallback mode (crafting etc.) the
        // generic grids own the slots. Pulse whichever we find. `super.pulseGridItem` can't be used
        // because the base defines it as an instance arrow field, not a prototype method.
        const slots: Graphics[] = []
        const modern = this.modernItemMap.get(itemId)
        if (modern) {
            slots.push(modern)
        } else {
            this.grids.forEach(grid => {
                const s = grid.itemMap.get(itemId)
                if (s) {
                    slots.push(s)
                }
            })
        }

        slots.forEach(slot => {
            const indicator = text("New!", "compactLabel", COLOR.gold, true)
            indicator.x = (slot.width - indicator.width) / 2
            indicator.y = 2
            slot.addChild(indicator)
            const throbber = new Throbber(slot, {
                expires: 10000,
                blinkInterval: 50,
                releaseCallback: () => slot.removeChild(indicator),
            })
            throbber.update()
        })
    }

    // ---- payload parsing ----------------------------------------------------

    private parse = (message: ScreenMessage): ParsedInventory => {
        const result: ParsedInventory = {
            backpack: [],
            readied: [],
            equipped: [],
            statLines: [],
            actions: [],
            gridCount: 0,
            hasBackpackGrid: false,
        }
        const grids: { items: ItemDetailMeta[]; rows: number; columns: number }[] = []

        const walk = (node: DynamicTextMeta | string) => {
            const meta: DynamicTextMeta = typeof node === "string" ? { text: node } : node
            if (!meta) {
                return
            }

            if (meta.type === "grid") {
                const items: ItemDetailMeta[] = meta.context?.gridMeta || []
                grids.push({ items, rows: meta.context?.rows ?? 0, columns: meta.context?.columns ?? 0 })
            } else if (meta.type === "entity") {
                if (!result.entity) {
                    result.entity = {
                        entityId: meta.context?.entityId,
                        appearance: meta.context?.appearance,
                        inventory: meta.context?.inventory,
                    }
                }
            } else if (meta.type === "button|dynamic" && meta.clickEventEmitter) {
                result.actions.push({ text: meta.text, event: meta.clickEventEmitter })
            } else if (meta.font === "ImportantFont" && meta.text) {
                if (!result.name) {
                    result.name = meta.text
                }
            } else if (meta.text && /^(HP:|Speed:|Defense:|Mana)/.test(meta.text)) {
                result.statLines.push(meta.text)
            }

            if (meta.children) {
                meta.children.forEach(walk)
            }
        }

        ;(message.dynamicText || []).forEach(walk)

        result.gridCount = grids.length

        // Classify grids by the server's dimensions (6x5 backpack, 6x1 readied, 2x6 equipped);
        // any unclassified grid folds into the backpack. We deliberately IGNORE these dims for
        // layout — they only tell us which bucket each pre-filtered item list belongs to.
        grids.forEach(grid => {
            if (grid.columns === 6 && grid.rows === 1) {
                result.readied.push(...grid.items)
            } else if (grid.columns === 2 && grid.rows === 6) {
                result.equipped.push(...grid.items)
            } else {
                if (grid.columns === 6 && grid.rows === 5) {
                    result.hasBackpackGrid = true
                }
                result.backpack.push(...grid.items)
            }
        })

        return result
    }

    // The modern layout is only meaningful for the player's own inventory panel. That panel is the
    // only reuse of this modal that carries BOTH the 6x5 backpack grid AND the character portrait
    // (showEntity). Crafting/trade sub-dialogs reuse the modal via updateContent but omit the
    // portrait, so they fall through to the generic renderer and look exactly as they always did.
    private isSelfInventory = (parsed: ParsedInventory): boolean => !!parsed.entity && parsed.hasBackpackGrid

    isInventoryOrCrafting = (): boolean =>
        !isTradeMessage(this.message) && (isCraftingMessage(this.message) || this.isSelfInventory(this.parse(this.message)))

    // The player's own pack, the crafting bench and a chest or trade screen all leave the world live
    // around them; a trade screen closes itself once what it is about leaves the player's view
    // (GuiManager.closeScreenOffSubject). Any other server dialog still closes the world off.
    leavesWorldLive = (): boolean => isTradeMessage(this.message) || this.isInventoryOrCrafting()

    // ---- panel chrome -------------------------------------------------------

    // The board: a dropped silhouette, a cut-timber frame with a bronze inner rim, the dark face, a
    // warmer band behind the title, one lit edge along the top and a rivet in each corner. Drawn the
    // way build_board.ts draws the build controls, because these panels and those controls are the
    // same camp's furniture.
    private drawPanelBg = (g: Graphics, h: number) => {
        const x = PANEL_INSET
        const y = PANEL_INSET
        const w = this.panelW - PANEL_INSET * 2
        const ph = h - PANEL_INSET * 2
        const frame = 4
        g.lineStyle(0)
        g.beginFill(0x000000, 0.36)
        chamfer(g, x, y + 4, w, ph, PANEL_CHAMFER)
        g.endFill()
        g.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha: 1, join: LINE_JOIN.MITER })
        g.beginFill(TIMBER, 1)
        chamfer(g, x, y, w, ph, PANEL_CHAMFER)
        g.endFill()
        g.lineStyle({ width: 1, color: RIM, alpha: 0.85, join: LINE_JOIN.MITER })
        g.beginFill(COLOR.panelFill, 0.985)
        chamfer(g, x + frame, y + frame, w - frame * 2, ph - frame * 2, PANEL_CHAMFER - 2)
        g.endFill()
        // The title band: the same cut at the top, square at the bottom where the groove runs. The trade
        // screen has no title -- it opens on the two parties -- so it has no band either.
        if (!isTradeMessage(this.message)) {
            const bx = x + frame + 1
            const by = y + frame + 1
            const bw = w - frame * 2 - 2
            const bc = PANEL_CHAMFER - 3
            g.lineStyle(0)
            g.beginFill(COLOR.panelFillTop, 0.85)
            g.moveTo(bx + bc, by).lineTo(bx + bw - bc, by).lineTo(bx + bw, by + bc)
                .lineTo(bx + bw, 40).lineTo(bx, 40).lineTo(bx, by + bc).closePath()
            g.endFill()
        }
        g.lineStyle(1, RIM_LIGHT, 0.2)
        g.moveTo(x + frame + PANEL_CHAMFER, y + frame + 1.5).lineTo(x + w - frame - PANEL_CHAMFER, y + frame + 1.5)
        g.lineStyle(0)
        // Rivets sit on the frame's corners, where the timber is.
        const r = frame / 2 + 0.5
        for (const [rx, ry] of [[x + PANEL_CHAMFER + 2, y + r], [x + w - PANEL_CHAMFER - 2, y + r],
            [x + PANEL_CHAMFER + 2, y + ph - r], [x + w - PANEL_CHAMFER - 2, y + ph - r]]) {
            rivet(g, rx, ry)
        }
    }

    // A screen's name: its glyph, then the name in letterspaced caps, then the groove that closes the
    // title band. The glyph is the one the command wheel uses for the same thing, so the pack looks
    // like the pack wherever it is drawn.
    private drawPanelTitle = (content: Graphics, label: string, icon: WheelIcon) => {
        const title = caps(label, "compactTitle", COLOR.title, 1.5)
        title.x = 36
        title.y = 14
        content.addChild(title)
        content.addChild(glyph(icon, 25, title.y + title.height / 2, 15, COLOR.gold))
        groove(content, 14, this.panelW - 14, 40)
        content.lineStyle(0)
    }

    /** The header row's room for the close disc at its end: the disc and a button gap. */
    private headerCloseRoom = (): number => closeControlRadius() * 2 + 6

    private drawTitleBar = (content: Graphics, parsed: ParsedInventory) => {
        this.drawPanelTitle(content, "Inventory", "sack")

        // Header actions (e.g. Craft) — out to the right margin, left of the close disc.
        let bx = this.panelW - HEADER_RIGHT_MARGIN - this.headerCloseRoom()
        parsed.actions.forEach(action => {
            const btnW = 64
            const button = makeButton(action.text, btnW, HEADER_BUTTON_H, () => {
                this.logic.playerController.client.emitPlayerEvent(action.event)
            })
            bx -= btnW
            button.x = bx
            button.y = HEADER_BUTTON_Y
            content.addChild(button)
            // The way through to the recipe list, and the second clue in the ember tutorial's chain.
            if (CRAFT_ACTION.test(action.text || "")) {
                this.clueAt("ember-crafting", button.x, button.y, btnW, 24)
            }
            bx -= 6
        })

        // The last clue in the chain: once the campfire is readied, the way out of the pack is the way
        // to put it down. The disc is the modal's (see closeAnchor), drawn at the end of this row, so
        // this reports where it is rather than drawing it. Reported only while the campfire is readied,
        // which is what makes reporting it proof that Ready was pressed.
        if (parsed.readied.some(item => (item.title || "").toLowerCase() === CLUED_ITEM)) {
            const r = closeControlRadius()
            this.clueAt("ember-close", this.panelW - HEADER_RIGHT_MARGIN - r * 2, HEADER_BUTTON_Y + HEADER_BUTTON_H / 2 - r, r * 2, r * 2)
        }
    }

    // ---- the profile row ---------------------------------------------------

    /**
     * Which item each gear socket shows. Worn gear comes from the equipped grid. The hand socket also
     * shows the weapon in hand when it lives in the ready bar (it is in both places, one item), or the
     * weapon put away while the hands carry a load, greyed out.
     */
    private profileSockets = (parsed: ParsedInventory): Partial<Record<EquipSlot, { item: ItemDetailMeta; worn: boolean }>> => {
        const sockets: Partial<Record<EquipSlot, { item: ItemDetailMeta; worn: boolean }>> = {}
        parsed.equipped.forEach(item => {
            if (item.equipSlot && !sockets[item.equipSlot]) sockets[item.equipSlot] = { item, worn: true }
        })
        if (!sockets.weapon) {
            const held = [...parsed.readied, ...parsed.backpack].find(item =>
                item.equipSlot === "weapon" && (item.inHand || item.stowed))
            if (held) sockets.weapon = { item: held, worn: false }
        }
        return sockets
    }

    private drawProfile = (
        content: Graphics,
        parsed: ParsedInventory,
        sockets: Partial<Record<EquipSlot, { item: ItemDetailMeta; worn: boolean }>>,
        card: ModernDetailCard,
    ) => {
        if (parsed.entity) {
            const entity = new Entity()
            entity.weapon = {
                weaponType: locateInventoryActiveItem(parsed.entity.inventory, "weapon")?.type as any,
            }
            entity.appearance = parsed.entity.appearance
            entity.inventory = parsed.entity.inventory
            const profile = new PackProfileCard(entity, parsed.entity.entityId, parsed.name)
            profile.position.set(PROFILE_CARD_X, PROFILE_Y)
            content.addChild(profile)
            this.profileCard = profile
            this.profileEntityId = parsed.entity.entityId
            this.profilePayloadHp = parseHp(parsed.statLines)
            this.updateProfile(0)
            this.startProfileTick()
        }

        const column = (kinds: EquipSlot[], x: number) => {
            const top = PROFILE_Y + (PROFILE_CARD_H - (kinds.length * SOCKET + (kinds.length - 1) * SOCKET_GAP)) / 2
            kinds.forEach((kind, i) => {
                const entry = sockets[kind]
                content.addChild(this.makeSlot(entry?.item, x, Math.round(top + i * (SOCKET + SOCKET_GAP)), card,
                    COLOR.equipped, SOCKET, false, "equipped", { socket: kind, worn: entry?.worn }))
            })
        }
        column(LEFT_SOCKETS, DOCK_MARGIN)
        column(RIGHT_SOCKETS, DOCK_W - DOCK_MARGIN - SOCKET)
        // A drop anywhere in the row -- on the card, between the sockets -- finds the item's own socket.
        this.itemSectionTarget(content, "equipped", new Rectangle(8, PROFILE_Y - 4, DOCK_W - 16, PROFILE_CARD_H + 8))
    }

    // The card's HP is read live off the player rather than the screen's payload: the world goes on
    // behind the pack, and a hit taken while it is open should land on the card as it lands on the
    // roster. Any other body's pack keeps the HP its screen was drawn with.
    private updateProfile = (dtMs: number) => {
        const card = this.profileCard
        if (!card || card.destroyed) return
        const player = this.logic.playerEntity
        const own = !!player && player.entityId === this.profileEntityId
        const hp = own ? player.hp : this.profilePayloadHp?.hp
        const hpMax = own ? player.maxHp : this.profilePayloadHp?.max
        if (hp !== undefined && this.profileHp !== undefined && hpMax && hp < this.profileHp) {
            this.profileFx.hit(PROFILE_FX_ID, this.profileHp, hp, hpMax)
        }
        this.profileHp = hp
        this.profileFx.update(dtMs)
        card.paint(hp, hpMax, this.profileFx.frame(PROFILE_FX_ID))
    }

    private startProfileTick = () => {
        const ticker = this.ticker()
        if (!ticker || this.profileTick) return
        this.profileTick = () => this.updateProfile(ticker.deltaMS)
        ticker.add(this.profileTick)
    }

    private stopProfileTick = () => {
        if (this.profileTick) this.ticker()?.remove(this.profileTick)
        this.profileTick = undefined
    }

    // ---- right region sections ---------------------------------------------

    // `note` is a muted right-aligned aside on the label row (e.g. the crafting pack's "+3 more"); the
    // rule stops short of it.
    private sectionLabel = (
        content: Graphics,
        label: string,
        x: number,
        y: number,
        note?: string,
        right = this.panelW - 16,
    ) => {
        const t = caps(label, "compactBody", COLOR.label, 1.2)
        t.x = x
        t.y = y
        content.addChild(t)

        let ruleRight = right
        if (note) {
            const n = text(note, "compactLabel", COLOR.textMuted)
            n.x = right - n.width
            n.y = y + 1
            content.addChild(n)
            ruleRight = n.x - 8
        }

        // An engraved groove out to the right of the label.
        groove(content, x + t.width + 8, ruleRight, Math.round(y + t.height / 2))
        content.lineStyle(0)
    }

    // Inventory sections and trade sides use the same drop region and highlight.
    private itemSectionTarget = (content: Graphics, section: ItemDropTarget, bounds: Rectangle) => {
        const highlight = new Graphics()
        highlight.interactive = false
        highlight.visible = false
        highlight.lineStyle({ width: 2, color: COLOR.gold, alpha: 1, join: LINE_JOIN.MITER })
        highlight.beginFill(COLOR.gold, 0.08)
        chamfer(highlight, bounds.x, bounds.y, bounds.width, bounds.height, 5)
        highlight.endFill()
        content.addChild(highlight)
        this.itemDrag?.registerSection(content, bounds, section, on => {
            highlight.visible = on
        })
    }

    private drawReadied = (content: Graphics, parsed: ParsedInventory, card: ModernDetailCard) => {
        const originX = 16
        const labelY = READIED_LABEL_Y
        this.sectionLabel(content, "Readied", originX, labelY, undefined, DOCK_W - 16)
        const slotsY = labelY + 18
        this.readiedForKeys = parsed.readied
        for (let i = 0; i < MAX_READIED_COUNT; i++) {
            const item = parsed.readied[i]
            const slot = this.makeSlot(item, originX + i * (DOCK_SLOT + 5), slotsY, card, COLOR.readied, DOCK_SLOT, false, "readied",
                { readyNumber: i + 1 })
            content.addChild(slot)
        }
        this.itemSectionTarget(content, "readied", new Rectangle(8, labelY - 4, DOCK_W - 16, 56))
    }

    private drawBackpack = (
        content: Graphics,
        parsed: ParsedInventory,
        card: ModernDetailCard,
        sockets: Partial<Record<EquipSlot, { item: ItemDetailMeta; worn: boolean }>> = {},
    ) => {
        const originX = this.horizontalPanel ? DOCK_W + 16 : 16
        const labelY = this.horizontalPanel ? 46 : BACKPACK_LABEL_Y
        this.sectionLabel(content, "Backpack", originX, labelY)
        const slotsY = labelY + 18

        // The weapon put away for a load sits in the hand socket, not here; anything worn that has no
        // socket of its own (a cape) is listed with the pack rather than vanishing.
        const inSockets = new Set(Object.values(sockets).map(entry => entry.item.id))
        const items = [
            ...parsed.equipped.filter(item => !inSockets.has(item.id)),
            ...parsed.backpack.filter(item => !inSockets.has(item.id)),
        ]
        const cols = 8
        const gap = 4
        const rows = Math.max(4, Math.ceil(items.length / cols))
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const idx = r * cols + c
                const item = items[idx]
                const slot = this.makeSlot(
                    item,
                    originX + c * (DOCK_SLOT + gap),
                    slotsY + r * (DOCK_SLOT + gap),
                    card,
                    COLOR.gold,
                    DOCK_SLOT,
                    true,
                    "backpack",
                )
                content.addChild(slot)
            }
        }
        this.itemSectionTarget(content, "backpack", new Rectangle(
            originX - 8, labelY - 4, DOCK_W - 16, 22 + rows * (DOCK_SLOT + gap),
        ))
    }

    // ---- individual slot ----------------------------------------------------

    private makeSlot = (
        item: ItemDetailMeta | undefined,
        x: number,
        y: number,
        card: ModernDetailCard,
        accent: number,
        size: number = SLOT,
        // Only the PLAYER'S OWN pack grid is clued. The same slot builder draws the equipped/readied
        // rows and both halves of a trade, and a campfire sitting in a chest is not the one the
        // tutorial is talking about.
        clued: boolean = false,
        section: ItemDropTarget = "backpack",
        extra: SlotExtras = {},
    ): Graphics => {
        const slot = new Graphics()
        slot.x = x
        slot.y = y

        // A socket carved into the board. An empty one is barely there, so the pack reads by what is
        // IN it; an item in use carries its section's ring (bronze worn, ember readied); the one under
        // the pointer is ringed in gold with a faint glow, the same gold the command wheel lights a
        // choice with.
        const drawFrame = (hovered: boolean) => {
            slot.clear()
            drawSocket(slot, 0, 0, size, size, item ? COLOR.slotFill : COLOR.slotEmpty)
            const active = !!item?.active
            if (hovered) {
                slot.lineStyle({ width: 4, color: COLOR.slotHover, alpha: 0.16, join: LINE_JOIN.MITER })
                chamfer(slot, -0.5, -0.5, size + 1, size + 1, SOCKET_CHAMFER + 1)
            }
            if (hovered || active) {
                slot.lineStyle({ width: 1.5, color: hovered ? COLOR.slotHover : accent, alpha: 1, join: LINE_JOIN.MITER })
                chamfer(slot, 0.75, 0.75, size - 1.5, size - 1.5, SOCKET_CHAMFER)
            } else if (item) {
                slot.lineStyle({ width: 1, color: COLOR.slotBorder, alpha: 0.9, join: LINE_JOIN.MITER })
                chamfer(slot, 0.5, 0.5, size - 1, size - 1, SOCKET_CHAMFER)
            }
            slot.lineStyle(0)
        }

        drawFrame(false)
        const socket = extra.socket
        this.itemDrag?.register(slot, item, section, size, drawFrame, socket ? {
            kind: socket,
            worn: extra.worn,
            prime: this.socketPrimer(slot, size),
            reject: () => this.shakeNode(slot),
        } : undefined)

        // The ready slots carry their hotkey, which is also what hover + a number key sends an item to.
        // Added last (see below), so it sits over the item's art.
        const readyNumber = () => {
            if (extra.readyNumber === undefined) return
            const n = text(`${extra.readyNumber}`, "overline", COLOR.label, true)
            n.alpha = 0.6
            n.position.set(3, 1)
            slot.addChild(n)
        }

        if (!item) {
            readyNumber()
            // An empty gear socket shows a faint outline of what goes in it, and names itself on hover.
            if (socket) {
                const ghost = new BasicSprite({ name: SOCKET_GHOSTS[socket], scale: 1, color: 0xffffff, animated: false, rotating: false })
                ghost.alpha = 0.2
                if (ghost.sprite) ghost.sprite.tint = RIM_LIGHT
                slot.addChild(ghost)
                fitItemIcon(ghost, 0, 0, size)
                if (!isMobile()) {
                    slot.on("mouseover", () => {
                        if (!this.itemDrag?.dragging) this.showNamePill(`${SOCKET_NAMES[socket]}`, slot.x, slot.y, size)
                    })
                    slot.on("mouseout", () => this.hideNamePill())
                }
            }
            return slot
        }

        this.modernItemMap.set(item.id, slot)

        // The fourth clue: the campfire they just made, in the pack it landed in.
        if (clued && (item.title || "").toLowerCase() === CLUED_ITEM) {
            this.clueAt("ember-pack", x, y, size, size)
        }

        for (const entry of SHELTER_CLUE_ITEMS) {
            if ("pack" in entry && clued && (item.title || "").toLowerCase() === entry.itemType) {
                this.clueAt(entry.pack, x, y, size, size)
            }
        }

        if (clued && (item.title || "").toLowerCase() === "wood") {
            this.clueAt("ember-wood-pack", x, y, size, size)
        }

        const sprite = new BasicSprite({
            name: item.spriteId,
            scale: (0.78 * size) / SLOT,
            color: 0xffffff,
            animated: false,
            rotating: false,
        })
        slot.addChild(sprite)
        fitItemIcon(sprite, 0, 0, size)
        // A weapon put away while the hands carry a load: greyed out in its socket (its ready-bar copy
        // is still a hotkey like any other).
        const stowed = !!item.stowed && !!socket
        if (stowed) {
            sprite.alpha = 0.38
            if (sprite.sprite) sprite.sprite.tint = 0xa0a0a0
        }

        let qtyWidth = 0
        if (item.quantity > 1) {
            const qty = text(`${item.quantity}`, "compactCaption", COLOR.text, true)
            qty.x = size - qty.width - 3
            qty.y = size - qty.height - 2
            qtyWidth = qty.width + 5
            slot.addChild(qty)
        }

        // The wear gauge, in the same place and at the same size as the readied tray's (gui/
        // durability.ts) -- the two are the same slot seen in two windows, and wear is the kind of
        // thing a player checks in one and acts on in the other.
        //
        // A child, not slot geometry: `drawFrame` reissues that geometry on every hover, and the
        // item art reaches to within 4px of the well's floor, which is where the gauge lives.
        const wear = WearBar.forSlot(item.condition, size, qtyWidth)
        if (wear) {
            if (stowed) wear.alpha = 0.35
            slot.addChild(wear)
        }

        // In the hands right now, marked on its ready-bar copy (the hand socket shows it as well).
        if (section === "readied" && item.inHand) {
            const dot = new Graphics()
            dot.lineStyle(1.5, COLOR.slotInset, 1)
            dot.beginFill(COLOR.readied, 1)
            dot.drawCircle(size - 1, 1, 3.5)
            dot.endFill()
            slot.addChild(dot)
        }
        readyNumber()

        slot.interactive = true
        slot.cursor = "grab"
        const onSelect: Callback<ItemDetailOption> = option => this.selectItemOption(item, option)
        const reveal = () => {
            if (this.itemDrag?.suppressClick(slot)) return
            drawFrame(true)
            this.hideNamePill()
            card.show(item, onSelect, slot.x, slot.y, size, this.panelW, this.panelH)
        }

        // HOVER NAMES IT, CLICK OPENS IT. The card used to spring open under the pointer on its way
        // across the grid, which made simply looking at your pack feel like being interrupted. A pill
        // with the item's name answers "what is that?" without answering a question nobody asked, and
        // the menu is now something you go and get.
        // Shift-click is the quick move (Minecraft's): see quickMoveOption.
        slot.on(isMobile() ? "tap" : "click", (event?: InteractionEvent) => {
            const shift = !!(event?.data?.originalEvent as MouseEvent | undefined)?.shiftKey
            if (shift && !isMobile() && !this.itemDrag?.suppressClick(slot)) {
                this.quickMove(item, section, slot)
                return
            }
            reveal()
        })
        if (!isMobile()) {
            slot.on("mouseover", () => {
                if (this.itemDrag?.dragging) return
                drawFrame(true)
                this.showNamePill(item.title, slot.x, slot.y, size)
                this.hoverForKeys(item, section)
            })
        }
        slot.on("mouseout", (event: InteractionEvent) => {
            drawFrame(false)
            this.hideNamePill()
            this.hoverForKeys(undefined)
            // The card still closes on its own terms: it has its own hover grace period, so travelling
            // from the slot to the menu that just opened does not shut it (see ModernDetailCard).
            card.scheduleHide(event)
        })

        return slot
    }

    // ---- gear sockets, quick moves, ready keys --------------------------------

    /** How a gear socket answers a drag (item_slot_drag primeSockets): lit, stepped back, or refusing. */
    private socketPrimer = (slot: Graphics, size: number) => {
        let mark: Graphics | undefined
        let last: SocketPrime | undefined
        return (state?: SocketPrime) => {
            if (state === last || slot.destroyed) return
            last = state
            slot.alpha = state === "dim" ? 0.45 : 1
            if (!mark) {
                mark = new Graphics()
                slot.addChild(mark)
            }
            mark.clear()
            if (state === "want" || state === "bad") {
                const color = state === "want" ? COLOR.slotHover : COLOR.shortfall
                mark.lineStyle({ width: 4, color, alpha: 0.22, join: LINE_JOIN.MITER })
                chamfer(mark, -1, -1, size + 2, size + 2, SOCKET_CHAMFER + 1)
                mark.lineStyle({ width: 2, color, alpha: 1, join: LINE_JOIN.MITER })
                chamfer(mark, 0.5, 0.5, size - 1, size - 1, SOCKET_CHAMFER)
            }
        }
    }

    /** A short side-to-side shake: "not here". Without a ticker (tests) it is skipped. */
    private shakeNode = (node: Container) => {
        const ticker = this.ticker()
        if (!ticker || node.destroyed) return
        const home = node.x
        let elapsed = 0
        const step = () => {
            elapsed += ticker.deltaMS
            if (node.destroyed || elapsed >= 320) {
                ticker.remove(step)
                if (!node.destroyed) node.x = home
                return
            }
            node.x = home + Math.sin(elapsed / 18) * 3 * (1 - elapsed / 320)
        }
        ticker.add(step)
    }

    /**
     * Shift-click, Minecraft's quick move. From the pack: gear and weapons to their own socket (swapping
     * with what is there, as a drop on the portrait does), anything else to the ready bar -- unless the
     * bar is full, where Minecraft leaves it be too. From the ready bar or a socket: back to the pack.
     * On a chest or trade screen: across to the other side (Take or Give).
     */
    private quickMoveOption = (item: ItemDetailMeta, section: ItemDropTarget): ItemDetailOption | undefined => {
        const drop = item.itemDropOptions
        if (section === "trade-target" || section === "trade-player") return drop?.["trade-target"] ?? drop?.["trade-player"]
        if (section === "backpack") {
            if (item.equipSlot && drop?.equipped) return drop.equipped
            return this.readiedForKeys.length < MAX_READIED_COUNT ? drop?.readied : undefined
        }
        return drop?.backpack
    }

    private quickMove = (item: ItemDetailMeta, section: ItemDropTarget, slot: Graphics) => {
        const option = this.quickMoveOption(item, section)
        if (!option) {
            this.shakeNode(slot)
            return
        }
        this.selectItemOption(item, option)
    }

    // The digits are the hotbar's, and the world stays live behind the pack, so the panel takes 1-6
    // only while the pointer is over one of its items -- and never from under the quantity picker.
    private claimsReadyKey = (key: string): boolean => READY_KEY.test(key)

    private hoverForKeys = (item?: ItemDetailMeta, section?: ItemDropTarget) => {
        const usable = !!item && !isMobile() && (section === "backpack" || section === "readied" || section === "equipped")
            && !isTradeMessage(this.message)
        this.keyHover = usable ? { item, section } : undefined
        const player = this.logic.playerController
        if (this.keyHover && !this.quantityPicker) {
            this.attachReadyKeys()
            player.claimKeys(this.claimsReadyKey)
            this.readyKeysClaimed = true
        } else if (this.readyKeysClaimed) {
            player.releaseKeys(this.claimsReadyKey)
            this.readyKeysClaimed = false
        }
    }

    private attachReadyKeys = () => {
        if (this.readyKeysAttached) return
        this.readyKeysAttached = true
        this.logic.clientRenderer.keyboard().addListener(this.onReadyKey)
    }

    private detachReadyKeys = () => {
        if (!this.readyKeysAttached) return
        this.readyKeysAttached = false
        this.logic.clientRenderer.keyboard().removeListener(this.onReadyKey)
    }

    // Hover + N sends the item to ready slot N, swapping out what is there. The bar packs to the left,
    // so a number past its end simply readies the item at the end.
    private onReadyKey = (key: string, down: boolean) => {
        const hover = this.keyHover
        if (!down || !hover || this.quantityPicker || this.itemDrag?.dragging || !READY_KEY.test(key)) return
        const occupant = this.readiedForKeys[Number(key) - 1]
        if (occupant?.id === hover.item.id) return
        const option = occupant
            ? hover.item.itemSlotDropOptions?.[occupant.id]
            : hover.section !== "readied" ? hover.item.itemDropOptions?.readied : undefined
        if (option) this.selectItemOption(hover.item, option)
    }

    // =========================================================================
    // CRAFTING — every recipe as a tile, grouped, over a bench holding the selected
    // one. Reuses this same modal (opened from the pack's Craft tab). What is shown
    // is crafting_catalog.ts, where it goes is crafting_grid.ts, and how it moves is
    // crafting_fx.ts.
    // =========================================================================

    private emit = (event?: PlayerEvent) => {
        if (event) {
            this.logic.playerController.client.emitPlayerEvent(event)
        }
    }

    /**
     * Take the reveal marker out of the stored message, so a LOCAL rebuild cannot see it twice.
     *
     * The panel re-reads `this.message` every time it redraws itself, and it redraws itself for
     * things the server knows nothing about. A one-shot therefore has to be consumed here as well as
     * on the server; the next real message brings its own marker if there is one.
     */
    private consumeCraftingReveal = () => {
        // `asMeta` hands back the very node for an object (it only wraps bare strings), so this edits
        // the stored message rather than a copy of it -- which is the point.
        consumeRevealMarker((this.message?.dynamicText || []).map(asMeta).filter(Boolean))
    }

    private parseCrafting = (message: ScreenMessage): ParsedCrafting => {
        const result: ParsedCrafting = { recipes: [], headerActions: [], empty: false }
        const top = (message.dynamicText || []).map(asMeta).filter(Boolean)

        // The craftable browser is the top-level container holding recipe rows (a "Craft" button
        // descendant) or the "None" placeholder when nothing is craftable.
        const craftable = top.find(
            n => n.type === "container" && (hasDescendantText(n, "Craft") || hasDescendantText(n, "None")),
        )

        // Header actions (the pack) -- button|dynamic buttons OUTSIDE the browser, so we don't scoop
        // up each recipe's Craft button.
        top.forEach(n => {
            if (n === craftable) {
                return
            }
            const collect = (node: DynamicTextMeta) => {
                if (node.type === "button|dynamic" && node.clickEventEmitter && node.text !== "Craft") {
                    result.headerActions.push({ text: node.text, event: node.clickEventEmitter })
                }
                ;(node.children || []).forEach(collect)
            }
            collect(n)
        })

        if (!craftable) {
            result.empty = true
            return result
        }

        // Recipe rows are a flat sequence of [Craft button, icon sprite, name node, spacer]. The name
        // node carries the recipe as data (`craftingRecipe`); the button carries the sealed craft.
        let cur: CraftRecipe | null = null
        ;(craftable.children || []).forEach(child => {
            if (child.type === "button|dynamic" && child.text === "Craft") {
                cur = {
                    key: "", itemType: "", title: "", group: "", cost: [], owned: 0, yield: 1, maxCrafts: 0,
                    enabled: !!child.clickEventEmitter && !(child.textDecorations || []).includes("disabled"),
                    event: child.clickEventEmitter,
                }
                result.recipes.push(cur)
            } else if (cur && child.type === "sprite") {
                cur.spriteId = child.spriteId
            } else if (child.context?.craftingReveal) {
                result.reveal = child.context.craftingReveal
            } else if (cur && !cur.key && child.text && child.text.trim() !== "" && child.text !== "Craft") {
                const recipe = child.context?.craftingRecipe || {}
                cur.key = child.text
                cur.itemType = recipe.itemType || child.text
                cur.title = recipe.title || prettify(recipe.name || child.text)
                cur.group = recipe.group || ""
                cur.family = recipe.family
                cur.familyTitle = recipe.familyTitle
                cur.variant = recipe.variant
                cur.description = recipe.description
                cur.full = recipe.full
                cur.cost = recipe.cost || []
                cur.owned = recipe.owned || 0
                cur.yield = recipe.yield || 1
                cur.maxCrafts = recipe.maxCrafts ?? (cur.enabled ? 1 : 0)
            }
        })
        result.recipes = result.recipes.filter(recipe => recipe.key)
        result.empty = result.recipes.length === 0
        return result
    }

    /** The recipe as the LATEST list has it. A held Craft button asks again before every repeat. */
    private latestRecipe = (key?: string): CraftRecipe | undefined =>
        key ? this.parseCrafting(this.message).recipes.find(recipe => recipe.key === key) : undefined

    private createCraftingContent = (): Graphics => {
        // Cleared before the rebuild so a handler can never be left pointing at a discarded tree.
        this.recipeStrip = undefined
        this.scrollThumb = undefined
        this.recipeMaxScroll = 0
        this.craftGrid = undefined
        this.railTabs = []
        this.pinnedHeaders = []
        this.craftTiles = new Map()
        this.craftIngredients = new Map()
        this.craftWell = undefined
        this.craftButtonRect = undefined
        this.craftButtonGlow = undefined
        this.packTabCenter = undefined
        this.packTabRect = undefined

        // A new list from the server, as opposed to a redraw the panel asked for itself: everything
        // the player had in flight has been answered, as far as the panel can tell.
        if (this.message !== this.craftingMessage) {
            this.craftingMessage = this.message
            this.craftsInFlight = 0
        }

        const bottom = (this.horizontalPanel ? MOBILE_CRAFT_BOTTOM : DOCK_H / MOBILE_PANEL_SCALE) - PANEL_BOTTOM_PAD
        const content = this.assemble(body => {
            const parsed = this.parseCrafting(this.message)
            this.drawCraftingTitleBar(body, parsed)

            // What the last list could not make and this one can lights up -- the planks just made
            // are what a wall was waiting for. Nothing lights on the first list the panel sees.
            const fresh = newlyEnabled(this.craftingEnabled, parsed.recipes)
            this.craftingEnabled = enabledKeys(parsed.recipes)
            fresh.forEach(recipe => this.startCraftGlow(familyOf(recipe)))
            if (fresh.some(recipe => recipe.key === this.craftingSelected)) {
                this.startCraftGlow(CRAFT_BUTTON_GLOW)
            }

            if (parsed.reveal) {
                this.revealRecipe(parsed.recipes, parsed.reveal)
                this.consumeCraftingReveal()
            }

            const selected = this.selectedRecipe(parsed.recipes)
            const regions = craftingRegions({ panelW: this.panelW, bottom, horizontal: this.horizontalPanel })
            const groups = buildCatalog({
                recipes: parsed.recipes,
                groups: CRAFTING_GROUPS.map(key => ({ key, label: CRAFTING_GROUP_LABELS[key] || prettify(key) })),
                selectedKey: selected?.key,
                picks: this.craftingPicks,
                query: this.craftingSearchOpen ? this.craftingQuery : "",
            })
            this.drawCraftingRail(body, regions.rail, groups)
            this.drawCraftingGrid(body, regions.grid, groups, parsed)
            this.drawBench(body, regions.bench, selected, parsed.recipes)
            this.reportRecipeClues(selected)
            this.reportPackClues(parsed.recipes)
        }, () => bottom + PANEL_BOTTOM_PAD)

        this.mountCraftFx(content)
        this.attachWheel()
        this.attachResize()
        return content
    }

    /** The selected recipe, or -- when there is none yet, or it left the list -- the first that can be made. */
    private selectedRecipe = (recipes: CraftRecipe[]): CraftRecipe | undefined => {
        let recipe = recipes.find(next => next.key === this.craftingSelected)
        if (!recipe) {
            recipe = recipes.find(next => next.enabled) ?? recipes[0]
            this.craftingSelected = recipe?.key
        }
        return recipe
    }

    private selectCraftRecipe = (recipe: CraftRecipe) => {
        this.stopCraftHold()
        this.craftingSelected = recipe.key
        if (recipe.family) {
            this.craftingPicks[recipe.family] = recipe.key
        }
    }

    /**
     * "Show them this recipe": a one-shot from the server (the ember tutorial pointing at the campfire)
     * or a tapped ingredient. Select it, take any search out of its way, bring its tile into view and
     * light it, so the player can see WHICH tile they were sent to.
     *
     * The marker names an item type ("planks"), or -- from an older server -- a row's name.
     */
    private revealRecipe = (recipes: CraftRecipe[], target: string) => {
        const recipe = recipes.find(next => next.key === target) ?? recipeForItem(recipes, target)
        if (!recipe) {
            return
        }
        this.selectCraftRecipe(recipe)
        this.closeCraftingSearch()
        this.craftingRevealFamily = familyOf(recipe)
        this.startCraftGlow(familyOf(recipe))
    }

    // A short ingredient that has a recipe of its own is a way to go and make it.
    private jumpToRecipe = (recipe: CraftRecipe) => {
        this.revealRecipe([recipe], recipe.key)
        this.refreshCrafting()
    }

    /**
     * Re-place the field when the window changes shape.
     *
     * The panel is laid out in a virtual 800x600 screen that is letterboxed into whatever the window
     * is, so a resize moves and rescales the box without redrawing anything. A DOM node pinned to
     * page coordinates does not follow on its own.
     */
    private attachResize = () => {
        if (this.resizeListener || typeof window === "undefined") {
            return
        }
        this.resizeListener = () => this.positionSearchField()
        window.addEventListener("resize", this.resizeListener)
    }

    private detachResize = () => {
        if (this.resizeListener && typeof window !== "undefined") {
            window.removeEventListener("resize", this.resizeListener)
        }
        this.resizeListener = undefined
    }

    /**
     * The title band: the screen's name, then [Pack][Craft] -- the two screens of one panel, the one
     * showing lit. Pack is the server's own "Inventory" action.
     */
    private drawCraftingTitleBar = (content: Graphics, parsed: ParsedCrafting) => {
        this.drawPanelTitle(content, "Crafting", "hammer")
        const tabW = 48
        const craftTab = makeButton("Craft", tabW, HEADER_BUTTON_H, () => undefined, { primary: true })
        craftTab.x = this.panelW - HEADER_RIGHT_MARGIN - this.headerCloseRoom() - tabW
        craftTab.y = HEADER_BUTTON_Y
        // The tab you are on is not a button.
        craftTab.interactive = false
        content.addChild(craftTab)

        const pack = parsed.headerActions.find(action => /inventory|pack/i.test(action.text || "")) ?? parsed.headerActions[0]
        if (!pack) {
            return
        }
        const packTab = makeButton("Pack", tabW, HEADER_BUTTON_H, () => this.emit(pack.event))
        packTab.x = craftTab.x - tabW - 4
        packTab.y = HEADER_BUTTON_Y
        content.addChild(packTab)
        this.packTabCenter = { x: packTab.x + tabW / 2, y: packTab.y + HEADER_BUTTON_H / 2 }
        this.packTabRect = { x: packTab.x, y: packTab.y, w: tabW, h: HEADER_BUTTON_H }

        // "+5": what the bench just put in the pack, for a moment after it lands.
        const badge = this.craftBadge
        if (badge && badge.count > 0) {
            const label = text(`+${badge.count}`, "compactCaption", COLOR.panelFill, true)
            const w = Math.ceil(label.width) + 8
            const pill = new Graphics()
            pill.beginFill(COLOR.readied, 1)
            pill.drawRoundedRect(0, 0, w, 12, 6)
            pill.endFill()
            label.x = 4
            label.y = (12 - label.height) / 2
            pill.addChild(label)
            pill.x = packTab.x - w + 6
            pill.y = packTab.y - 5
            content.addChild(pill)
        }
    }

    /**
     * The rail: one tab per group on the grid, lit for the group in view, and a magnifier at its end.
     * A tab is a jump, not a filter -- the grid always holds everything. With the search open, the box
     * takes the rail's place.
     */
    private drawCraftingRail = (content: Graphics, rect: Rect, groups: CatalogGroup[]) => {
        const toggleW = rect.h + 4
        const toggle = new Graphics()
        drawSocket(toggle, 0, 0, toggleW, rect.h, this.craftingSearchOpen ? COLOR.slotFill : COLOR.slotEmpty, 3)
        if (this.craftingSearchOpen) {
            const x = new Graphics()
            x.lineStyle({ width: 1.5, color: COLOR.label, alpha: 1, cap: LINE_CAP.ROUND })
            x.moveTo(-3.5, -3.5).lineTo(3.5, 3.5).moveTo(3.5, -3.5).lineTo(-3.5, 3.5)
            x.position.set(toggleW / 2, rect.h / 2)
            toggle.addChild(x)
        } else {
            toggle.addChild(glyph("lens", toggleW / 2, rect.h / 2, 11, COLOR.label))
        }
        toggle.x = rect.x + rect.w - toggleW
        toggle.y = rect.y
        toggle.interactive = true
        toggle.cursor = "pointer"
        toggle.hitArea = new Rectangle(0, 0, toggleW, rect.h)
        toggle.on("pointertap", () => {
            if (this.craftingSearchOpen) {
                this.closeCraftingSearch()
            } else {
                this.craftingSearchOpen = true
                // Focused once the field is actually over its box (positionSearchField).
                this.focusSearchOnShow = true
            }
            this.craftingScrollY = 0
            this.refreshCrafting()
        })
        content.addChild(toggle)

        if (this.craftingSearchOpen) {
            this.drawCraftingSearchBox(content, rect.x, rect.y, rect.w - toggleW - 4, rect.h)
            return
        }
        this.searchField?.hide()

        let x = rect.x
        const limit = toggle.x - 4
        groups.forEach(group => {
            const ready = group.key === READY_GROUP
            const label = text(ready ? READY_LABEL : group.label, "compactCaption", COLOR.text, true)
            const dot = ready ? 7 : 0
            const w = RAIL_TAB_PAD * 2 + dot + Math.ceil(label.width)
            // Out of room: the group is still on the grid, a scroll away.
            if (x + w > limit) {
                disposeTextTextures(label)
                return
            }
            const tab = new Graphics()
            tab.beginFill(0xffffff, 0.001)
            tab.drawRect(0, 0, w, rect.h)
            tab.endFill()
            if (ready) {
                tab.beginFill(COLOR.readied, 1)
                tab.drawCircle(RAIL_TAB_PAD + 2.5, rect.h / 2, 2.5)
                tab.endFill()
            }
            label.x = RAIL_TAB_PAD + dot
            label.y = (rect.h - label.height) / 2 - 1
            tab.addChild(label)
            const bar = new Graphics()
            bar.beginFill(COLOR.gold, 1)
            bar.drawRect(RAIL_TAB_PAD - 1, rect.h - 2, w - RAIL_TAB_PAD * 2 + 2, 2)
            bar.endFill()
            tab.addChild(bar)
            tab.x = x
            tab.y = rect.y
            tab.interactive = true
            tab.cursor = "pointer"
            tab.on("pointertap", () => this.jumpToGroup(group.key))
            content.addChild(tab)
            this.railTabs.push({ key: group.key, label, bar })
            x += w + 2
        })
    }

    /** The search box in the rail's place: a groove cut into the board, the real <input> over it. */
    private drawCraftingSearchBox = (content: Graphics, x: number, y: number, w: number, h: number) => {
        const box = new Graphics()
        drawSocket(box, 0, 0, w, h, COLOR.slotEmpty, 4)
        if (this.craftingSearchFocused) {
            box.lineStyle({ width: 1.5, color: COLOR.slotHover, alpha: 1, join: LINE_JOIN.MITER })
            chamfer(box, 0.75, 0.75, w - 1.5, h - 1.5, 4)
            box.lineStyle(0)
        }
        box.addChild(glyph("lens", h / 2 + 1, h / 2, 11, this.craftingSearchFocused ? COLOR.gold : COLOR.textMuted))
        box.x = x
        box.y = y
        // Reserve this area for the DOM input, including the brief gap before it is positioned.
        box.interactive = true
        box.cursor = "text"
        content.addChild(box)
        // The box is only a FRAME. A real DOM <input> sits over it carrying the text and the caret --
        // the one thing that raises a phone's keyboard. See search_field.ts.
        this.placeSearchField(box, w, h)
    }

    private closeCraftingSearch = () => {
        this.craftingSearchOpen = false
        this.craftingQuery = ""
        this.applySearchFocus(false)
        this.searchField?.hide()
    }

    /**
     * Put the real <input> over the frame the panel just drew, and keep it there.
     *
     * Called on every rebuild, because the box moves: the panel is redrawn for every server message,
     * every scroll and every keystroke. The NODE is not rebuilt with it -- it is parented to the page,
     * not to the panel -- which is the whole reason this is a bare DOM element rather than a
     * pixi-text-input like the chat composer's. That library parents its node to a display object and
     * re-appends it whenever that object is re-added, so a field inside this panel would lose focus
     * after a single letter.
     */
    private placeSearchField = (box: Graphics, boxW: number, boxH: number) => {
        this.searchField = this.searchField ?? this.makeSearchField()
        this.searchBox = { box, w: boxW, h: boxH }
        // DEFERRED, and this is the whole subtlety. `assemble` builds the panel and hands it back for
        // the caller to add to the surface, so while this runs the box has no path to the stage at
        // all -- getGlobalPosition() reports a position inside a detached subtree, missing the dock
        // offset that puts the panel on the right-hand side of the screen.
        setTimeout(this.positionSearchField, 0)
    }

    private positionSearchField = () => {
        const held = this.searchBox
        const field = this.searchField
        const view = this.canvas()
        if (!held || !field || !view || held.box.destroyed || !this.craftingSearchOpen) {
            return
        }
        // Include the panel and stage scales when placing the DOM input over its canvas frame.
        const global = held.box.toGlobal({ x: 0, y: 0 })
        const end = held.box.toGlobal({ x: held.w, y: held.h })
        const rect = overlayRect(
            { x: global.x, y: global.y, width: end.x - global.x, height: end.y - global.y },
            this.rendererScreen(),
            view.getBoundingClientRect(),
        )
        this.searchRect = rect
        field.show(rect, this.craftingQuery)
        if (this.focusSearchOnShow) {
            this.focusSearchOnShow = false
            field.focus()
        }
    }

    private makeSearchField = (): SearchField =>
        new SearchField({
            placeholder: "Search recipes or materials…",
            leadingInset: SEARCH_GLYPH_INSET,
            ariaLabel: "Search recipes",
            onFocus: () => {
                // The game must stop reading the keyboard the moment the field takes it, not on the
                // first letter -- otherwise typing "w" walks the player north as well.
                if (this.applySearchFocus(true)) {
                    this.refreshCrafting()
                }
            },
            onInput: value => {
                this.craftingQuery = value
                // A narrowed list is a different list; keeping the old offset can leave it blank.
                this.craftingScrollY = 0
                this.craftingSearchFocused = true
                this.refreshCrafting()
            },
            onBlur: () => {
                if (!this.craftingSearchFocused) {
                    return
                }
                this.craftingSearchFocused = false
                this.logic.playerController.enabledKeyboardAction(true)
                this.modal?.suspendEscape(false)
                this.refreshCrafting()
            },
        })

    /**
     * Move the keyboard between the player and the search box, WITHOUT redrawing.
     *
     * Split out because the two callers that matter are already mid-redraw: a reveal clears the
     * search while the panel is being built, and the close handler runs while it is being torn down.
     * Asking either of them for a rebuild is at best wasted and at worst re-entrant.
     *
     * Returns whether anything changed, so the caller that CAN redraw only does so when it needs to.
     */
    private applySearchFocus = (focused: boolean): boolean => {
        if (focused === this.craftingSearchFocused) {
            return false
        }
        this.craftingSearchFocused = focused
        if (focused) {
            this.logic.playerController.enabledKeyboardAction(false)
            this.modal?.suspendEscape(true)
        } else {
            this.modal?.suspendEscape(false)
            this.logic.playerController.enabledKeyboardAction(true)
            this.searchField?.blurNode()
        }
        return true
    }

    private rendererScreen = (): { width: number; height: number } => {
        try {
            const screen = getRendererApp().renderer.screen
            return { width: screen.width, height: screen.height }
        } catch (e) {
            return { width: 0, height: 0 }
        }
    }

    /**
     * The grid: every group's tiles in a window that scrolls, the bench below never moving.
     *
     * Every tile is drawn, not just the visible ones, so a gesture moves the list by shifting one
     * container instead of rebuilding the panel -- rebuilding mid-drag destroys the very display
     * object handling the drag.
     */
    private drawCraftingGrid = (content: Graphics, rect: Rect, groups: CatalogGroup[], parsed: ParsedCrafting) => {
        if (groups.length < 1) {
            const why = parsed.empty ? "No recipes available yet." : `No recipe matches "${this.craftingQuery}".`
            const msg = text(why, "compactBody", COLOR.textMuted)
            msg.x = rect.x
            msg.y = rect.y + 6
            content.addChild(msg)
            return
        }

        const labels = groups.map(group => caps(group.key === READY_GROUP ? "Ready now" : group.label, "compactCaption",
            group.key === READY_GROUP ? COLOR.readied : COLOR.label, 1.2))
        const counts = groups.map(group => text(`${group.tiles.length}`, "compactCaption", COLOR.textMuted))
        const plan = (width: number) => layoutGrid(groups.map((group, i) => ({
            key: group.key,
            count: group.tiles.length,
            labelW: Math.ceil(labels[i].width + 4 + counts[i].width) + 4,
        })), width)
        let layout = plan(rect.w)
        if (layout.contentH > rect.h) {
            // The scrollbar takes a sliver of the width, and only when there is something to scroll.
            layout = plan(rect.w - SCROLLBAR_W - 6)
        }
        const viewH = rect.h
        const maxScroll = maxScrollFor(layout, viewH)
        this.craftGrid = { layout, rect, viewH, groups }
        if (this.craftingRevealFamily) {
            this.craftingScrollY = this.scrollToFamily(this.craftingRevealFamily, this.craftingScrollY)
            this.craftingRevealFamily = undefined
        }
        this.craftingScrollY = clampScroll(this.craftingScrollY, maxScroll)

        const view = new Container()
        view.x = rect.x
        view.y = rect.y
        content.addChild(view)

        // The part that actually moves.
        const strip = new Container()
        view.addChild(strip)
        this.recipeStrip = strip
        this.recipeMaxScroll = maxScroll
        const grooves = new Graphics()
        strip.addChild(grooves)

        groups.forEach((group, gi) => {
            const box = layout.boxes[gi]
            this.drawGroupHeader(strip, grooves, box, labels[gi], counts[gi])
            group.tiles.forEach((tile, i) => {
                const at = tileAt(box, i)
                this.drawCraftTile(strip, tile, at.x, at.y, group.key)
            })
        })
        strip.y = -this.craftingScrollY

        // A line's labels, pinned to the top of the window once its own have scrolled away.
        layout.lines.forEach(line => {
            const band = new Graphics()
            band.beginFill(COLOR.panelFill, 1)
            band.drawRect(0, 0, rect.w, HEADER_H + 2)
            band.endFill()
            groove(band, 0, rect.w - (maxScroll > 0 ? SCROLLBAR_W + 6 : 0), HEADER_H + 1.5)
            band.lineStyle(0)
            line.keys.forEach(key => {
                const gi = groups.findIndex(group => group.key === key)
                const pinned = caps(gi >= 0 && groups[gi].key === READY_GROUP ? "Ready now" : groups[gi]?.label || key,
                    "compactCaption", key === READY_GROUP ? COLOR.readied : COLOR.label, 1.2)
                pinned.x = layout.boxes[gi]?.x ?? 0
                pinned.y = (HEADER_H - pinned.height) / 2
                band.addChild(pinned)
            })
            band.visible = false
            view.addChild(band)
            this.pinnedHeaders.push({ line, band })
        })

        const mask = new Graphics()
        mask.beginFill(0xffffff, 1)
        // A little room either side for a lit tile's glow.
        mask.drawRect(-4, 0, rect.w + 8, viewH)
        mask.endFill()
        view.addChild(mask)
        view.mask = mask

        // Drag anywhere in the grid, including across a tile. Bound to the VIEW rather than a surface
        // laid over the tiles, so a press still reaches the tile underneath; pixi bubbles the pointer
        // events up to here either way.
        view.interactive = true
        view.hitArea = new Rectangle(0, 0, rect.w, viewH)
        this.bindRecipeDrag(view)

        if (maxScroll > 0) {
            this.drawScrollbar(content, rect.x + rect.w - SCROLLBAR_W, rect.y, viewH, layout.contentH)
        }
        this.updateCraftingScroll()
    }

    private drawGroupHeader = (strip: Container, grooves: Graphics, box: GridGroupBox, label: Text, count: Text) => {
        label.x = box.x
        label.y = box.y + (HEADER_H - label.height) / 2 - 1
        strip.addChild(label)
        count.x = label.x + label.width + 4
        count.y = label.y
        strip.addChild(count)
        const from = count.x + count.width + 6
        if (box.x + box.w - from > 8) {
            groove(grooves, from, box.x + box.w, Math.round(box.y + HEADER_H / 2))
            grooves.lineStyle(0)
        }
    }

    /**
     * One tile: a socket with the item in it. Lit when something on it can be made, the selected one
     * rimmed in gold, a count of what is carried, a sliver of progress toward an unaffordable price,
     * and a pip per kind when it folds several recipes together.
     */
    private drawCraftTile = (strip: Container, tile: CatalogTile, x: number, y: number, groupKey: string) => {
        const selected = tile.members.some(member => member.key === this.craftingSelected)

        // The glow sits BEHIND the tile, so it reads as light coming off it rather than a border on it.
        // Three fattening, fading strokes rather than a filter: a filter is a render pass per object.
        const glow = new Graphics()
        for (let ring = 3; ring >= 1; ring--) {
            glow.lineStyle({ width: ring * 2, color: COLOR.slotHover, alpha: 0.16 * (4 - ring), join: LINE_JOIN.MITER })
            chamfer(glow, x - ring, y - ring, TILE + ring * 2, TILE + ring * 2, SOCKET_CHAMFER + ring)
        }
        glow.lineStyle(0)
        glow.alpha = this.craftGlowAlpha(tile.family)
        glow.visible = glow.alpha > 0
        strip.addChild(glow)

        const g = new Graphics()
        g.x = x
        g.y = y
        drawSocket(g, 0, 0, TILE, TILE, selected ? SELECTED_FACE : tile.lit ? COLOR.slotFill : COLOR.slotEmpty)
        g.lineStyle({
            width: selected ? 1.5 : 1,
            color: selected ? COLOR.slotHover : tile.lit ? RIM : COLOR.slotBorder,
            alpha: selected ? 1 : 0.85,
            join: LINE_JOIN.MITER,
        })
        chamfer(g, 0.5, 0.5, TILE - 1, TILE - 1, SOCKET_CHAMFER)
        g.lineStyle(0)

        if (tile.shown.spriteId) {
            const sprite = new BasicSprite({
                name: tile.shown.spriteId,
                scale: (TILE / 52) * 0.95,
                color: tile.lit ? 0xffffff : DIM_ICON_TINT,
                animated: false,
                rotating: false,
            })
            if (!tile.lit) {
                sprite.alpha = 0.75
            }
            g.addChild(sprite)
            fitItemIcon(sprite, 0, 0, TILE)
        }

        // A pip per kind, lit for each kind that can be made now.
        if (tile.members.length > 1) {
            tile.members.slice(0, 6).forEach((member, i) => {
                g.beginFill(member.enabled ? COLOR.label : COLOR.slotBorder, 1)
                g.drawRect(4 + i * 4, 4, 3, 3)
                g.endFill()
            })
        }

        // How close an unaffordable price is: something to go out and finish.
        if (!tile.lit && tile.progress > 0) {
            g.beginFill(COLOR.slotInset, 1)
            g.drawRect(5, TILE - 5, TILE - 10, 2)
            g.endFill()
            g.beginFill(RIM, 1)
            g.drawRect(5, TILE - 5, (TILE - 10) * tile.progress, 2)
            g.endFill()
        }

        let count: Text | undefined
        if (tile.owned > 0) {
            count = text(`${tile.owned}`, "compactCaption", COLOR.text, true)
            count.anchor.set(1, 1)
            count.x = TILE - 3
            count.y = TILE - 1
            count.scale.set(popScale(Date.now() - (this.craftPops.get(`tile:${tile.family}`) ?? -Infinity)))
            g.addChild(count)
        }

        g.interactive = true
        g.cursor = "pointer"
        g.hitArea = new Rectangle(0, 0, TILE, TILE)
        g.on("pointerover", () => {
            const grid = this.craftGrid
            if (grid && !this.recipeDragMoved) {
                this.showNamePill(tile.title, grid.rect.x + x, grid.rect.y + y - this.craftingScrollY, TILE)
            }
        })
        g.on("pointerout", this.hideNamePill)
        g.on("pointertap", () => {
            if (this.recipeDragMoved) {
                return
            }
            this.hideNamePill()
            this.selectCraftRecipe(tile.shown)
            this.refreshCrafting()
        })
        strip.addChild(g)

        const placed = this.craftTiles.get(tile.family) || []
        placed.push({ x, y, group: groupKey, itemType: tile.shown.itemType, glow, count })
        this.craftTiles.set(tile.family, placed)
    }

    /** Move the grid to a new offset without rebuilding the panel. */
    private scrollRecipesTo = (next: number) => {
        const clamped = clampScroll(next, this.recipeMaxScroll)
        if (clamped === this.craftingScrollY) {
            return
        }
        this.craftingScrollY = clamped
        if (this.recipeStrip) {
            this.recipeStrip.y = -clamped
        }
        this.positionScrollThumb()
        this.updateCraftingScroll()
    }

    /** What the scroll position means for the rail and the pinned labels. Touches no Text. */
    private updateCraftingScroll = () => {
        const grid = this.craftGrid
        if (!grid) {
            return
        }
        const active = activeGroup(grid.layout, this.craftingScrollY, grid.viewH)
        this.railTabs.forEach(tab => {
            const on = tab.key === active
            tab.label.alpha = on ? 1 : 0.55
            tab.bar.visible = on
        })
        const pinned = pinnedLine(grid.layout, this.craftingScrollY)
        this.pinnedHeaders.forEach(({ line, band }) => {
            band.visible = line === pinned
        })
    }

    private jumpToGroup = (key: string) => {
        const grid = this.craftGrid
        if (!grid) {
            return
        }
        this.tweenCraftScroll(scrollForGroup(grid.layout, key, grid.viewH))
    }

    /**
     * The scroll that shows a family's tile: none, if a copy of it (the Ready shelf's or its own) is
     * already on screen; otherwise its home group's.
     */
    private scrollToFamily = (family: string, scrollY: number): number => {
        const grid = this.craftGrid
        if (!grid) {
            return scrollY
        }
        const places: { key: string; index: number }[] = []
        grid.groups.forEach(group => group.tiles.forEach((tile, index) => {
            if (tile.family === family) {
                places.push({ key: group.key, index })
            }
        }))
        const visible = places.some(place => {
            const box = grid.layout.boxes.find(b => b.key === place.key)
            return box && tileVisible(tileAt(box, place.index).y, scrollY, grid.viewH)
        })
        if (visible || places.length < 1) {
            return scrollY
        }
        const home = places.find(place => place.key !== READY_GROUP) ?? places[0]
        return scrollToShow(grid.layout, home.key, home.index, scrollY, grid.viewH)
    }

    /**
     * The bar beside a scrolling grid -- the part that says there is more, and the part you grab.
     * Wide enough to actually hit with a pointer; the track is clickable too, so a tap below the thumb
     * jumps a window down rather than requiring a drag.
     */
    private drawScrollbar = (content: Graphics, x: number, y: number, viewH: number, contentH: number): void => {
        const track = new Graphics()
        drawSocket(track, 0, 0, SCROLLBAR_W, viewH, COLOR.slotEmpty, 3)
        track.x = x
        track.y = y
        track.interactive = true
        track.cursor = "pointer"
        content.addChild(track)

        const thumbH = Math.max(SCROLLBAR_MIN_THUMB, (viewH * viewH) / contentH)
        // A bronze bar riding in the groove, lit along its top.
        const thumb = new Graphics()
        thumb.lineStyle({ width: 1, color: TIMBER_EDGE, alpha: 1, join: LINE_JOIN.MITER })
        thumb.beginFill(RIM, 1)
        chamfer(thumb, 1, 1, SCROLLBAR_W - 2, thumbH - 2, 2)
        thumb.endFill()
        thumb.lineStyle(1, RIM_LIGHT, 0.7).moveTo(3, 2.5).lineTo(SCROLLBAR_W - 3, 2.5)
        thumb.lineStyle(0)
        thumb.interactive = true
        thumb.cursor = "pointer"
        track.addChild(thumb)

        this.scrollThumb = thumb
        this.scrollTravel = Math.max(0, viewH - thumbH)
        this.positionScrollThumb()

        // Drag the thumb: travel maps to scroll offset, not 1:1 with the pointer.
        let grabbed: number | null = null
        let grabbedScroll = 0
        thumb.on("pointerdown", (event: InteractionEvent) => {
            grabbed = event.data.global.y
            grabbedScroll = this.craftingScrollY
            event.stopPropagation()
        })
        const moveThumb = (event: InteractionEvent) => {
            if (grabbed === null || this.scrollTravel <= 0) {
                return
            }
            const travelled = event.data.global.y - grabbed
            this.scrollRecipesTo(grabbedScroll + (travelled / this.scrollTravel) * this.recipeMaxScroll)
        }
        const releaseThumb = () => {
            grabbed = null
        }
        thumb.on("pointermove", moveThumb)
        thumb.on("pointerup", releaseThumb)
        thumb.on("pointerupoutside", releaseThumb)
        // The pointer leaves a 10px-wide thumb almost immediately during a drag, so the move has to be
        // followed on the track behind it as well.
        track.on("pointermove", moveThumb)
        track.on("pointerup", releaseThumb)
        track.on("pointerupoutside", releaseThumb)

        // A tap on the track above or below the thumb pages towards it.
        track.on("pointertap", (event: InteractionEvent) => {
            const local = event.data.getLocalPosition(track)
            if (local.y >= thumb.y && local.y <= thumb.y + thumbH) {
                return
            }
            this.tweenCraftScroll(this.craftingScrollY + (local.y < thumb.y ? -viewH : viewH))
        })
    }

    private positionScrollThumb = () => {
        if (!this.scrollThumb) {
            return
        }
        const progress = this.recipeMaxScroll > 0 ? this.craftingScrollY / this.recipeMaxScroll : 0
        this.scrollThumb.y = progress * this.scrollTravel
    }

    /**
     * Press and drag the grid itself.
     *
     * The drag threshold matters: the tiles are inside the area being dragged, and pixi delivers a
     * tile's own tap after the gesture ends. The flag is checked by the tile rather than the event
     * being cancelled, because by then it is too late to cancel.
     */
    private bindRecipeDrag = (view: Container): void => {
        let from: number | null = null
        let fromScroll = 0
        let blurred = false

        view.on("pointerdown", (event: InteractionEvent) => {
            // Reaching for the grid means you have finished typing. The REDRAW that shows the box
            // unfocused waits for the gesture to end: rebuilding here would destroy this very view in
            // the middle of dispatching its own pointerdown.
            blurred = this.applySearchFocus(false)
            from = event.data.global.y
            fromScroll = this.craftingScrollY
            this.recipeDragMoved = false
            this.craftScrollTween = undefined
        })
        view.on("pointermove", (event: InteractionEvent) => {
            if (from === null) {
                return
            }
            const travelled = from - event.data.global.y
            if (Math.abs(travelled) > DRAG_SLOP) {
                this.recipeDragMoved = true
                this.hideNamePill()
            }
            this.scrollRecipesTo(fromScroll + travelled)
        })
        const up = () => {
            from = null
            // Cleared a frame later: a tile's own tap lands after pointerup, and it is that tap the
            // flag exists to swallow.
            setTimeout(() => {
                this.recipeDragMoved = false
                if (blurred) {
                    blurred = false
                    this.refreshCrafting()
                }
            }, 0)
        }
        view.on("pointerup", up)
        view.on("pointerupoutside", up)
    }

    // ---- the bench ---------------------------------------------------------------------------------

    /**
     * The selected recipe, on a board of its own: the item large in its well, its name and what it is
     * for, the kinds a folded tile holds, every ingredient as a tile of its own, and the action --
     * Craft (hold for more) and All, or, when it cannot be made, why not and the step that would fix it.
     */
    private drawBench = (content: Graphics, rect: Rect, recipe: CraftRecipe | undefined, recipes: CraftRecipe[]) => {
        const board = new Graphics()
        drawPlate(board, rect.x - 6, rect.y, rect.w + 12, rect.h, {
            face: COLOR.panelFillTop, faceAlpha: 0.85, rim: COLOR.panelBorder, rimAlpha: 0.9, shadow: false,
        })
        content.addChild(board)
        if (!recipe) {
            return
        }
        const lit = recipe.enabled
        const x = rect.x
        const w = rect.w
        const nameOf = (itemType: string) => this.craftItemName(itemType, recipes)
        let y = rect.y + BENCH_PAD

        // --- the item, and its words
        const wellY = y + (BENCH_TOP_H - BENCH_WELL) / 2
        const well = new Graphics()
        drawSocket(well, x, wellY, BENCH_WELL, BENCH_WELL, lit ? COLOR.slotFill : COLOR.slotEmpty)
        well.lineStyle({ width: 1, color: lit ? RIM : COLOR.slotBorder, alpha: 0.85, join: LINE_JOIN.MITER })
        chamfer(well, x + 0.5, wellY + 0.5, BENCH_WELL - 1, BENCH_WELL - 1, SOCKET_CHAMFER)
        well.lineStyle(0)
        content.addChild(well)
        if (recipe.spriteId) {
            const sprite = new BasicSprite({
                name: recipe.spriteId,
                scale: (BENCH_WELL / 52) * 0.95,
                color: lit ? 0xffffff : DIM_ICON_TINT,
                animated: false,
                rotating: false,
            })
            content.addChild(sprite)
            fitItemIcon(sprite, x, wellY, BENCH_WELL)
            this.craftWell = { x: x + BENCH_WELL / 2, y: wellY + BENCH_WELL / 2, size: BENCH_WELL * 0.8, sprite }
        }

        const tx = x + BENCH_WELL + 8
        const tw = x + w - tx
        const name = text(recipe.title, "compactTitle", COLOR.title, true)
        name.x = tx
        name.y = y
        content.addChild(name)
        const baseline = name.y + name.height
        if (recipe.yield > 1) {
            const makes = text(`makes ${recipe.yield}`, "compactCaption", COLOR.textMuted)
            makes.x = name.x + name.width + 6
            makes.y = baseline - makes.height - 1
            content.addChild(makes)
        }
        const have = text(`have ${recipe.owned}`, "compactCaption", COLOR.textMuted)
        have.x = tx + tw - have.width
        have.y = baseline - have.height - 1
        content.addChild(have)

        const kinds = recipe.family ? recipes.filter(next => next.family === recipe.family) : []
        const chipH = 14
        if (recipe.description) {
            const lines = kinds.length > 1 ? 1 : 2
            const desc = clampedText(recipe.description, "compactCaption", COLOR.textMuted, tw, lines)
            desc.x = tx
            desc.y = baseline + 2
            content.addChild(desc)
        }
        // The kinds a folded tile holds, picked here.
        if (kinds.length > 1) {
            let cx = tx
            const cy = y + BENCH_TOP_H - chipH
            kinds.forEach(kind => {
                const on = kind.key === recipe.key
                const label = text(kind.variant || kind.title, "compactCaption",
                    on ? COLOR.text : kind.enabled ? COLOR.label : COLOR.textMuted, on)
                const cw = Math.ceil(label.width) + 10
                if (cx + cw > tx + tw) {
                    disposeTextTextures(label)
                    return
                }
                const chip = new Graphics()
                drawPlate(chip, 0, 0, cw, chipH, {
                    face: on ? SELECTED_FACE : COLOR.slotEmpty,
                    rim: on ? COLOR.slotHover : kind.enabled ? RIM : COLOR.slotBorder,
                    rimAlpha: 0.9, c: 3, shadow: false,
                })
                label.x = 5
                label.y = (chipH - label.height) / 2
                chip.addChild(label)
                chip.x = cx
                chip.y = cy
                chip.interactive = true
                chip.cursor = "pointer"
                chip.on("pointertap", () => {
                    this.selectCraftRecipe(kind)
                    this.refreshCrafting()
                })
                content.addChild(chip)
                cx += cw + 4
            })
        }
        y += BENCH_TOP_H + BENCH_ROW_GAP

        // --- what it needs: each ingredient a tile, the short ones in red
        const needs = caps("Needs", "compactCaption", COLOR.label, 1.2)
        needs.x = x
        needs.y = y + (BENCH_ING - needs.height) / 2
        content.addChild(needs)
        let ix = x + Math.max(Math.ceil(needs.width) + 8, 46)
        recipe.cost.forEach(cost => {
            const short = isShort(cost)
            const maker = short ? recipes.find(next => next.itemType === cost.itemType) : undefined
            const tileG = new Graphics()
            tileG.x = ix
            tileG.y = y
            drawSocket(tileG, 0, 0, BENCH_ING, BENCH_ING, COLOR.slotFill)
            tileG.lineStyle({ width: 1, color: short ? COLOR.shortfall : RIM, alpha: short ? 0.9 : 0.6, join: LINE_JOIN.MITER })
            chamfer(tileG, 0.5, 0.5, BENCH_ING - 1, BENCH_ING - 1, SOCKET_CHAMFER)
            tileG.lineStyle(0)
            if (cost.spriteId) {
                const sprite = new BasicSprite({ name: cost.spriteId, scale: (BENCH_ING / 52) * 0.95, color: 0xffffff, animated: false, rotating: false })
                tileG.addChild(sprite)
                fitItemIcon(sprite, 0, -3, BENCH_ING)
            }
            const count = text(`${Math.min(cost.held, cost.required)}/${cost.required}`, "compactCaption", short ? COLOR.shortfall : COLOR.text, true)
            count.anchor.set(0.5, 1)
            count.x = BENCH_ING / 2
            count.y = BENCH_ING
            count.scale.set(popScale(Date.now() - (this.craftPops.get(`ing:${cost.itemType}`) ?? -Infinity)))
            tileG.addChild(count)
            if (maker) {
                // A way to go and make it: a small arrow out of the corner.
                tileG.lineStyle({ width: 1.2, color: COLOR.shortfall, alpha: 1, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
                tileG.moveTo(BENCH_ING - 9, 8).lineTo(BENCH_ING - 4, 3)
                tileG.moveTo(BENCH_ING - 8, 3).lineTo(BENCH_ING - 4, 3).lineTo(BENCH_ING - 4, 7)
                tileG.lineStyle(0)
            }
            const at = ix
            tileG.interactive = true
            tileG.cursor = maker ? "pointer" : "default"
            tileG.hitArea = new Rectangle(0, 0, BENCH_ING, BENCH_ING)
            const note = maker ? " · tap to make" : short && cost.source ? ` · ${cost.source}` : ""
            tileG.on("pointerover", () => this.showNamePill(`${nameOf(cost.itemType)}: ${cost.held} of ${cost.required}${note}`, at, y, BENCH_ING))
            tileG.on("pointerout", this.hideNamePill)
            if (maker) {
                tileG.on("pointertap", () => {
                    this.hideNamePill()
                    this.jumpToRecipe(maker)
                })
            }
            content.addChild(tileG)
            this.craftIngredients.set(cost.itemType, { x: ix + BENCH_ING / 2, y: y + BENCH_ING / 2, count })
            ix += BENCH_ING + 4
        })
        const hint = sourceLine(recipe, nameOf)
        if (hint && x + w - ix > 40) {
            const line = clampedText(hint, "compactCaption", COLOR.textMuted, x + w - ix - 4, 2)
            line.x = ix + 2
            line.y = y + (BENCH_ING - line.height) / 2
            content.addChild(line)
        }
        y += BENCH_ING + BENCH_ROW_GAP

        // --- the action
        const h = BENCH_ACTION_H
        if (lit) {
            const allW = recipe.maxCrafts > 1 ? 62 : 0
            const craftW = w - (allW ? allW + 6 : 0)
            // A recipe that has just become makeable lights its button too.
            const glow = new Graphics()
            for (let ring = 3; ring >= 1; ring--) {
                glow.lineStyle({ width: ring * 2, color: COLOR.slotHover, alpha: 0.16 * (4 - ring), join: LINE_JOIN.MITER })
                chamfer(glow, x - ring, y - ring, craftW + ring * 2, h + ring * 2, PLATE_CHAMFER + ring)
            }
            glow.lineStyle(0)
            glow.alpha = this.craftGlowAlpha(CRAFT_BUTTON_GLOW)
            glow.visible = glow.alpha > 0
            content.addChild(glow)
            this.craftButtonGlow = glow

            // Press to make one; hold and it keeps going, faster, for as long as the pack covers it.
            // The craft fires on the PRESS (pointertap would wait for the release), and the hold is
            // let go from the window, because a rebuild can replace this button mid-hold.
            const craft = makeButton("Craft", craftW, h, () => undefined, { primary: true })
            craft.x = x
            craft.y = y
            craft.on("pointerdown", (event: InteractionEvent) => {
                if ((event.data?.button ?? 0) > 0) {
                    return
                }
                this.hideNamePill()
                this.startCraftHold(recipe.key)
            })
            craft.on("pointerover", () => this.showNamePill("Hold to keep crafting", x + craftW / 2 - 10, y, 20))
            craft.on("pointerout", this.hideNamePill)
            content.addChild(craft)
            this.craftButtonRect = { x, y, w: craftW, h }

            if (allW) {
                const all = makeButton(`All ×${recipe.maxCrafts}`, allW, h, () => this.craftAll(recipe.key))
                all.x = x + craftW + 6
                all.y = y
                content.addChild(all)
            }
        } else {
            const step = firstStep(recipe, recipes)
            const stepLabel = step ? `Make ${step.title} first` : ""
            const stepW = step ? Math.min(Math.round(w * 0.58), Math.max(104, stepLabel.length * 6 + 18)) : 0
            const sunkW = w - (stepW ? stepW + 6 : 0)
            const sunk = new Graphics()
            drawSocket(sunk, x, y, sunkW, h, COLOR.slotEmpty, 4)
            content.addChild(sunk)
            const why = clampedText(needLine(recipe, nameOf) || "Not yet", "compactCaption", COLOR.textMuted, sunkW - 10, 2)
            why.x = x + (sunkW - why.width) / 2
            why.y = y + (h - why.height) / 2
            content.addChild(why)
            this.craftButtonRect = { x, y, w: sunkW, h }
            if (step) {
                const make = makeButton(stepLabel, stepW, h, () => this.craftFirstStep(step.key), { primary: true })
                make.x = x + sunkW + 6
                make.y = y
                content.addChild(make)
            }
        }
    }

    /** What the bench calls an item: its recipe's name when it has one on the list, else its type. */
    private craftItemName = (itemType: string, recipes: CraftRecipe[]): string =>
        recipes.find(recipe => recipe.itemType === itemType)?.title ?? prettify(itemType)

    /**
     * The ember tutorial's recipe clues. The note goes on the bench's Craft button when the recipe it
     * names is the one on the bench (a reveal puts it there), and on its tile otherwise -- pointing at
     * the tile says "pick this", pointing at the button says "make it".
     */
    private reportRecipeClues = (selected?: CraftRecipe) => {
        const clued: { itemType: string; type: EmberClueType }[] = [
            { itemType: CLUED_ITEM, type: "ember-recipe" },
            ...SHELTER_CLUE_ITEMS.map(entry => ({ itemType: entry.itemType as string, type: entry.recipe as EmberClueType })),
        ]
        const grid = this.craftGrid
        clued.forEach(({ itemType, type }) => {
            const button = this.craftButtonRect
            if (selected && selected.itemType.toLowerCase() === itemType && button) {
                this.clueAt(type, button.x, button.y, button.w, button.h, { x: button.x, y: button.y, w: button.w, h: button.h })
                return
            }
            if (!grid) {
                return
            }
            for (const placed of this.craftTiles.values()) {
                const tile = placed.find(p => p.itemType.toLowerCase() === itemType && tileVisible(p.y, this.craftingScrollY, grid.viewH))
                if (tile) {
                    const px = grid.rect.x + tile.x
                    const py = grid.rect.y + tile.y - this.craftingScrollY
                    this.clueAt(type, px, py, TILE, TILE, { x: px, y: py, w: TILE, h: TILE })
                    return
                }
            }
        })
    }

    /**
     * The tutorial's next step after a clued craft is the item in the pack ("Here it is, in your pack",
     * then Ready). This screen has no pack grid -- the pack is one tab away -- so once the item exists,
     * the clue points at the Pack tab with words for that, and follows the player to the item's own
     * slot when the pack opens. Reported only once one has been made, which is what makes drawing it
     * proof the craft happened (it retires the recipe clue, as the pack slot did).
     */
    private reportPackClues = (recipes: CraftRecipe[]) => {
        const tab = this.packTabRect
        if (!tab) {
            return
        }
        const owned = (itemType: string) => recipes.filter(recipe => recipe.itemType.toLowerCase() === itemType)
            .reduce((sum, recipe) => sum + recipe.owned, 0)
        PACK_TAB_CLUES.forEach(({ itemType, type, lines }) => {
            if (owned(itemType) > 0) {
                this.clueAt(type, tab.x, tab.y, tab.w, tab.h, undefined, { lines, placement: isMobile() ? "below" : "left" })
            }
        })
    }

    // Is a tutorial clue asking the player to make this one? Then a press makes exactly one: a held
    // button that ran on would spend the wood the lesson needs next.
    private recipeClueArmed = (recipe: CraftRecipe): boolean => {
        const clues = this.guiManager.tutorialClues() as { isClueArmed?: (type: EmberClueType) => boolean }
        const type = recipe.itemType.toLowerCase() === CLUED_ITEM ? "ember-recipe"
            : SHELTER_CLUE_ITEMS.find(entry => entry.itemType === recipe.itemType.toLowerCase())?.recipe
        return !!type && !!clues.isClueArmed?.(type as EmberClueType)
    }

    // ---- making things ----------------------------------------------------------------------------

    /**
     * One craft of the recipe as the latest list has it -- and never more crafts in flight than that
     * list said the pack covers, so a held button cannot run ahead of the server and play a flight
     * for a craft that will be refused.
     */
    private craftOnce = (key: string): boolean => {
        const recipe = this.latestRecipe(key)
        if (!recipe?.enabled || !recipe.event || this.craftsInFlight >= Math.max(1, recipe.maxCrafts)) {
            return false
        }
        this.craftsInFlight++
        this.emit(recipe.event)
        this.launchCraftFx(recipe, 1, "craft")
        return true
    }

    // Everything the pack covers, in one request the server runs as a batch (`times`).
    private craftAll = (key: string) => {
        const recipe = this.latestRecipe(key)
        const n = recipe ? recipe.maxCrafts - this.craftsInFlight : 0
        if (!recipe?.enabled || !recipe.event || n < 1) {
            return
        }
        this.craftsInFlight += n
        this.emitWithQuantity(recipe.event, n)
        this.launchCraftFx(recipe, n, "craft")
    }

    // "Make Planks first": the next step toward the recipe on the bench, made without leaving it.
    private craftFirstStep = (key: string) => {
        const step = this.latestRecipe(key)
        if (!step?.enabled || !step.event) {
            return
        }
        this.emit(step.event)
        this.launchCraftFx(step, 1, "step")
    }

    private startCraftHold = (key: string) => {
        this.stopCraftHold()
        if (!this.craftOnce(key)) {
            return
        }
        const recipe = this.latestRecipe(key)
        if (recipe && this.recipeClueArmed(recipe)) {
            return
        }
        this.craftHold = { key, delay: HOLD_FIRST_MS }
        if (typeof window !== "undefined") {
            window.addEventListener("pointerup", this.stopCraftHold)
            window.addEventListener("pointercancel", this.stopCraftHold)
            window.addEventListener("blur", this.stopCraftHold)
        }
        this.craftHoldTimer = setTimeout(this.craftHoldTick, HOLD_FIRST_MS)
    }

    private craftHoldTick = () => {
        const hold = this.craftHold
        if (!hold) {
            return
        }
        if (!this.craftOnce(hold.key)) {
            this.stopCraftHold()
            return
        }
        hold.delay = nextHoldDelay(hold.delay)
        this.craftHoldTimer = setTimeout(this.craftHoldTick, hold.delay)
    }

    private stopCraftHold = () => {
        if (this.craftHoldTimer) {
            clearTimeout(this.craftHoldTimer)
        }
        this.craftHoldTimer = undefined
        this.craftHold = undefined
        if (typeof window !== "undefined") {
            window.removeEventListener("pointerup", this.stopCraftHold)
            window.removeEventListener("pointercancel", this.stopCraftHold)
            window.removeEventListener("blur", this.stopCraftHold)
        }
    }

    // ---- the craft moment: sparks, the flight, the landing ---------------------------------------
    //
    // On a layer that SURVIVES the panel's rebuilds -- the server answers every craft with a new list,
    // usually while the item is still in the air. It is moved onto each new panel as it is built, and
    // holds no Text, because a rebuild destroys every Text in the old tree (disposeTextTextures).
    // Targets are looked up every frame, so a tile that moved under a rebuild is still where the item
    // lands.

    private mountCraftFx = (wrapper: Graphics) => {
        this.craftFx = this.craftFx ?? new Container()
        // Over the body, under the hover pill and the clues.
        wrapper.addChildAt(this.craftFx, Math.min(2, wrapper.children.length))
    }

    private launchCraftFx = (recipe: CraftRecipe, crafts: number, kind: "craft" | "step") => {
        const layer = this.craftFx
        if (!layer || !recipe.spriteId) {
            return
        }
        const now = Date.now()
        const flights = Math.max(1, Math.min(crafts, MAX_BATCH_FLIGHTS))
        const family = familyOf(recipe)
        let from: { x: number; y: number; size: number } | undefined
        if (kind === "step") {
            const tile = this.visibleCraftTile(family)
            from = tile ? { x: tile.x + TILE / 2, y: tile.y + TILE / 2, size: TILE * 0.8 } : this.craftWell
        } else {
            from = this.craftWell
            this.throwSparks(now)
        }
        if (!from) {
            return
        }
        const start = from
        for (let i = 0; i < flights; i++) {
            const sprite = new BasicSprite({ name: recipe.spriteId, scale: 1, color: 0xffffff, animated: false, rotating: false })
            const holder = new Container()
            holder.addChild(sprite)
            // Centred on the holder, so the holder's scale grows it about its middle.
            fitItemIcon(sprite, -start.size / 2, -start.size / 2, start.size)
            holder.visible = false
            layer.addChild(holder)
            // Each landing is one craft's worth; a batch shares its total out over its flights.
            const share = i < flights - 1 ? Math.floor((crafts * recipe.yield) / flights) : crafts * recipe.yield - Math.floor((crafts * recipe.yield) / flights) * (flights - 1)
            this.craftFlights.push({
                node: holder,
                from: { x: start.x, y: start.y },
                start: now + i * FLIGHT_STAGGER_MS,
                size: start.size,
                target: kind === "step" ? { ingredient: recipe.itemType } : { family },
                itemType: recipe.itemType,
                landed: share,
            })
        }
        this.ensureCraftFxTick()
    }

    private throwSparks = (now: number) => {
        const layer = this.craftFx
        const well = this.craftWell
        if (!layer || !well) {
            return
        }
        for (let i = 0; i < SPARK_COUNT; i++) {
            const dot = new Graphics()
            dot.beginFill(i % 3 === 0 ? COLOR.readied : COLOR.slotHover, 1)
            dot.drawCircle(0, 0, 1.6 + (i % 3) * 0.5)
            dot.endFill()
            layer.addChild(dot)
            this.craftSparks.push({
                node: dot,
                origin: { x: well.x, y: well.y },
                angle: (i / SPARK_COUNT) * Math.PI * 2 + Math.random() * 0.6,
                reach: well.size * 0.55 + Math.random() * 12,
                start: now,
                duration: 360 + Math.random() * 200,
            })
        }
    }

    /** A tile of this family that is on screen now, in panel coordinates. */
    private visibleCraftTile = (family: string): { x: number; y: number } | undefined => {
        const grid = this.craftGrid
        const placed = this.craftTiles.get(family)
        if (!grid || !placed) {
            return undefined
        }
        const tile = placed.find(p => tileVisible(p.y, this.craftingScrollY, grid.viewH))
        return tile ? { x: grid.rect.x + tile.x, y: grid.rect.y + tile.y - this.craftingScrollY } : undefined
    }

    // Where a flight lands this frame: its tile if one is on screen, the ingredient slot it was made
    // for, or the Pack tab -- it is in the pack either way.
    private craftFlightTarget = (target: CraftFlight["target"]): { x: number; y: number } | undefined => {
        if ("ingredient" in target) {
            const slot = this.craftIngredients.get(target.ingredient)
            if (slot) {
                return { x: slot.x, y: slot.y }
            }
        } else {
            const tile = this.visibleCraftTile(target.family)
            if (tile) {
                return { x: tile.x + TILE / 2, y: tile.y + TILE / 2 }
            }
        }
        return this.packTabCenter
    }

    private landCraftFlight = (flight: CraftFlight) => {
        const now = Date.now()
        if ("ingredient" in flight.target) {
            this.craftPops.set(`ing:${flight.target.ingredient}`, now)
        } else {
            this.craftPops.set(`tile:${flight.target.family}`, now)
            this.startCraftGlowFor(flight.target.family, LANDING_GLOW_MS)
        }
        // The pickup pop, climbing its ladder through a run of landings.
        try {
            ;(this.logic as any).audio?.pickup?.({ type: flight.itemType })
        } catch (e) {
            // A missing sound costs the sound, not the craft.
        }
        this.craftBadge = { count: (this.craftBadge?.count ?? 0) + flight.landed, until: now + BADGE_MS }
        this.craftBadgeDirty = true
    }

    private ensureCraftFxTick = () => {
        if (this.craftFxTick) {
            return
        }
        const ticker = this.ticker()
        if (!ticker) {
            return
        }
        this.craftFxTick = () => this.stepCraftFx()
        ticker.add(this.craftFxTick)
    }

    /** One frame of everything in motion. Stops itself once nothing is. */
    private stepCraftFx = () => {
        // Turned to the pack mid-flight: there is nothing left to land on.
        if (!isCraftingMessage(this.message)) {
            this.stopCraftFx()
            return
        }
        const now = Date.now()

        this.craftFlights = this.craftFlights.filter(flight => {
            const elapsed = now - flight.start
            if (elapsed < 0) {
                return true
            }
            const to = this.craftFlightTarget(flight.target)
            if (!to || flight.node.destroyed) {
                flight.node.destroy({ children: true })
                return false
            }
            const endScale = (("ingredient" in flight.target ? BENCH_ING : TILE) * 0.8) / Math.max(1, flight.size)
            const at = flightAt(flight.from, to, elapsed, endScale)
            flight.node.visible = true
            flight.node.position.set(at.x, at.y)
            flight.node.scale.set(at.scale)
            if (elapsed >= FLIGHT_MS) {
                flight.node.destroy({ children: true })
                this.landCraftFlight(flight)
                return false
            }
            return true
        })

        this.craftSparks = this.craftSparks.filter(spark => {
            const elapsed = now - spark.start
            if (elapsed >= spark.duration || spark.node.destroyed) {
                spark.node.destroy()
                return false
            }
            const at = sparkAt(spark.origin, spark.angle, spark.reach, elapsed, spark.duration)
            spark.node.position.set(at.x, at.y)
            spark.node.alpha = at.alpha
            spark.node.scale.set(at.scale)
            return true
        })

        // The counts that something just landed on.
        this.craftPops.forEach((startedAt, key) => {
            const scale = popScale(now - startedAt)
            const node = key.startsWith("ing:")
                ? this.craftIngredients.get(key.slice(4))?.count
                : undefined
            if (node && !node.destroyed) {
                node.scale.set(scale)
            } else if (key.startsWith("tile:")) {
                ;(this.craftTiles.get(key.slice(5)) || []).forEach(tile => {
                    if (tile.count && !tile.count.destroyed) tile.count.scale.set(scale)
                })
            }
            if (now - startedAt >= POP_MS) {
                this.craftPops.delete(key)
            }
        })

        // Glows: brightness off the curve, applied to whichever nodes the last build drew.
        this.craftGlows.forEach((glow, key) => {
            const alpha = glowAlpha(now - glow.start, glow.duration)
            const nodes = key === CRAFT_BUTTON_GLOW
                ? [this.craftButtonGlow]
                : (this.craftTiles.get(key) || []).map(tile => tile.glow)
            nodes.forEach(node => {
                if (node && !node.destroyed) {
                    node.alpha = alpha
                    node.visible = alpha > 0
                }
            })
            if (now - glow.start >= glow.duration) {
                this.craftGlows.delete(key)
            }
        })

        if (this.craftScrollTween) {
            const tween = this.craftScrollTween
            const t = Math.min(1, (now - tween.start) / SCROLL_TWEEN_MS)
            const eased = 1 - Math.pow(1 - t, 3)
            this.scrollRecipesTo(tween.from + (tween.to - tween.from) * eased)
            if (t >= 1) {
                this.craftScrollTween = undefined
            }
        }

        // The Pack tab's "+N" is drawn by the title bar; it changes rarely enough to redraw for.
        if (this.craftBadge && now >= this.craftBadge.until) {
            this.craftBadge = undefined
            this.craftBadgeDirty = true
        }
        if (this.craftBadgeDirty) {
            this.craftBadgeDirty = false
            this.refreshCrafting()
        }

        const busy = this.craftFlights.length || this.craftSparks.length || this.craftPops.size || this.craftGlows.size
            || this.craftScrollTween || this.craftBadge
        if (!busy) {
            this.stopCraftFx(false)
        }
    }

    private tweenCraftScroll = (to: number) => {
        const target = clampScroll(to, this.recipeMaxScroll)
        if (!this.ticker()) {
            this.scrollRecipesTo(target)
            return
        }
        this.craftScrollTween = { from: this.craftingScrollY, to: target, start: Date.now() }
        this.ensureCraftFxTick()
    }

    private startCraftGlow = (key: string) => this.startCraftGlowFor(key, GLOW_MS)

    private startCraftGlowFor = (key: string, duration: number) => {
        this.craftGlows.set(key, { start: Date.now(), duration })
        this.ensureCraftFxTick()
    }

    private craftGlowAlpha = (key: string): number => {
        const glow = this.craftGlows.get(key)
        return glow ? glowAlpha(Date.now() - glow.start, glow.duration) : 0
    }

    /**
     * Stop everything moving. `clear` (closing the panel) also drops what was in flight; the ticker
     * stopping itself when idle leaves state alone.
     */
    private stopCraftFx = (clear = true) => {
        if (this.craftFxTick) {
            this.ticker()?.remove(this.craftFxTick)
        }
        this.craftFxTick = undefined
        if (clear) {
            this.craftFlights.forEach(flight => flight.node.destroyed || flight.node.destroy({ children: true }))
            this.craftSparks.forEach(spark => spark.node.destroyed || spark.node.destroy())
            this.craftFlights = []
            this.craftSparks = []
            this.craftPops.clear()
            this.craftGlows.clear()
            this.craftScrollTween = undefined
            this.craftBadge = undefined
        }
    }

    private ticker = () => {
        try {
            return getRendererApp().ticker
        } catch (e) {
            return undefined
        }
    }

    /**
     * The mouse wheel -- and a two-finger trackpad drag, which is the same event.
     *
     * pixi 6's InteractionManager handles pointer, mouse and touch events but NOT `wheel`: an
     * `on("wheel")` on a display object is simply never called. It has to come off the DOM canvas.
     *
     * NOT filtered by pointer position: nothing else in the client listens for a wheel, so while this
     * panel is up with a grid that can scroll, the wheel is unambiguously the grid's.
     *
     * Installed once per dialog and removed on close, so a stale listener cannot outlive the panel and
     * scroll a grid that is no longer on screen.
     */
    private attachWheel = () => {
        const view = this.canvas()
        if (!view || this.wheelListener) {
            return
        }
        this.wheelListener = (event: WheelEvent) => {
            if (this.recipeMaxScroll <= 0 || !this.recipeStrip) {
                return
            }
            event.preventDefault()
            this.craftScrollTween = undefined
            this.scrollRecipesTo(this.craftingScrollY + event.deltaY)
        }
        view.addEventListener("wheel", this.wheelListener, { passive: false })
    }

    private detachWheel = () => {
        const view = this.canvas()
        if (view && this.wheelListener) {
            view.removeEventListener("wheel", this.wheelListener)
        }
        this.wheelListener = undefined
    }

    // The pixi canvas, via the two-function module that exists so callers needing only the
    // Application do not drag the whole renderer's import graph along. Never throws out here: a
    // missing canvas costs the wheel, not the panel.
    private canvas = (): HTMLCanvasElement | undefined => {
        try {
            return getRendererApp().view
        } catch (e) {
            return undefined
        }
    }

    // =========================================================================
    // TRANSFER — two side-by-side inventories (chest / trade). Each item carries
    // its own Take / Give options; hovering a slot surfaces them.
    // =========================================================================

    private parseTrade = (message: ScreenMessage): ParsedTrade => {
        const left: TradeSide = { items: [], statLines: [] }
        const right: TradeSide = { items: [], statLines: [] }
        const headerActions: { text: string; event: PlayerEvent }[] = []

        const collect = (node: DynamicTextMeta, side: TradeSide) => {
            if (node.type === "grid") {
                side.items.push(...(node.context?.gridMeta || []))
            } else if (node.type === "entity") {
                if (!side.entity) {
                    side.entity = {
                        entityId: node.context?.entityId,
                        appearance: node.context?.appearance,
                        inventory: node.context?.inventory,
                    }
                }
            } else if (node.type === "sprite" && node.spriteId && node.spriteId !== "action-trade.png") {
                if (!side.spriteId) {
                    side.spriteId = node.spriteId
                }
            } else if (node.type === "button|dynamic" && node.clickEventEmitter) {
                headerActions.push({ text: node.text, event: node.clickEventEmitter })
            } else if (node.font === "ImportantFont" && node.text) {
                if (!side.name) {
                    side.name = node.text
                }
            } else if (node.text && /^(HP:|Speed:|Defense:|Mana)/.test(node.text)) {
                side.statLines.push(node.text)
            }
            ;(node.children || []).forEach(c => collect(c, side))
        }

        ;(message.dynamicText || [])
            .map(asMeta)
            .filter(Boolean)
            .forEach(node => {
                if (node.type === "sprite" && node.spriteId === "action-trade.png") {
                    return
                }
                // The server positions the target (chest) on the left and the origin (player) on the
                // right; bucket every node by its x anchor.
                const x = node.exactLocation?.x ?? 0
                collect(node, x < 350 ? left : right)
            })

        return { left, right, headerActions }
    }

    private createTradeContent = (): Graphics =>
        this.assemble((body, card) => {
            const parsed = this.parseTrade(this.message)

            // Header action (e.g. "Inventory" when trading with a humanoid) top-right at the margin.
            let bx = this.panelW - 16
            parsed.headerActions.forEach(action => {
                const btnW = 82
                const button = makeButton(action.text, btnW, 24, () => this.emit(action.event))
                bx -= btnW
                button.x = bx
                button.y = 11
                body.addChild(button)
                bx -= 6
            })

            if (this.horizontalPanel) {
                const y = 48
                const leftBottom = this.drawTradeSection(body, parsed.left, y, card, COLOR.gold, "trade-target")
                const rightBottom = this.drawTradeSection(body, parsed.right, y, card, COLOR.readied, "trade-player", DOCK_W + 32)
                this.drawTradeArrow(body, this.panelW / 2, (y + Math.max(leftBottom, rightBottom)) / 2)
            } else {
                let y = this.drawTradeSection(body, parsed.left, 14, card, COLOR.gold, "trade-target")
                this.drawTradeArrow(body, this.panelW / 2, y + 24)
                y += 48
                this.drawTradeSection(body, parsed.right, y, card, COLOR.readied, "trade-player")
            }
        })

    // One stacked inventory: [thumbnail][name (+ compact stats)] header, then a grid of its items.
    private drawTradeSection = (
        content: Graphics,
        side: TradeSide,
        y: number,
        card: ModernDetailCard,
        accent: number,
        target: ItemDropTarget,
        originX = 0,
    ): number => {
        const margin = originX + 16
        const thumb = 40
        this.drawTradeThumb(content, side, margin, y, thumb)

        const nameX = margin + thumb + 10
        const name = text(side.name || "", "label", COLOR.title, true)
        name.x = nameX
        name.y = y + 2
        content.addChild(name)

        // Compact HP line for humanoid sides (chests have no stats).
        const statLine = side.statLines
            .find(l => l.startsWith("HP:"))
            ?.replace("HP: ", "HP ")
            .replace(/\s*\/\s*/, "/")
        if (statLine) {
            const s = text(statLine, "compactLabel", COLOR.textMuted)
            s.x = nameX
            s.y = y + 22
            content.addChild(s)
        }

        // Item grid.
        const gridY = y + thumb + 8
        const cols = 8
        const gap = 4
        const rows = Math.max(2, Math.ceil(side.items.length / cols))
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const item = side.items[r * cols + c]
                const slot = this.makeSlot(
                    item,
                    margin + c * (DOCK_SLOT + gap),
                    gridY + r * (DOCK_SLOT + gap),
                    card,
                    accent,
                    DOCK_SLOT,
                    false,
                    target,
                )
                content.addChild(slot)
            }
        }

        const bottom = gridY + rows * (DOCK_SLOT + gap)
        this.itemSectionTarget(content, target, new Rectangle(originX + 8, y - 4, DOCK_W - 16, bottom - y + 4))
        return bottom
    }

    // Small framed thumbnail: the player's portrait, or a chest's item sprite.
    private drawTradeThumb = (content: Graphics, side: TradeSide, x: number, y: number, size: number) => {
        drawSocket(content, x, y, size, size, 0x17110c, 5)
        content.lineStyle({ width: 1, color: RIM, alpha: 0.55, join: LINE_JOIN.MITER })
        chamfer(content, x - 0.5, y - 0.5, size + 1, size + 1, 5)
        content.lineStyle(0)

        if (side.entity?.appearance || side.entity?.inventory) {
            const entity = new Entity()
            entity.weapon = { weaponType: locateInventoryActiveItem(side.entity.inventory, "weapon")?.type as any }
            entity.appearance = side.entity.appearance
            entity.inventory = side.entity.inventory

            const displayer = new EntityDisplayer({ entityId: side.entity.entityId, scale: 1.4 }, entity)
            const portrait = displayer.createContent()
            content.addChild(portrait)

            const b = portrait.getLocalBounds()
            if (b.width > 0 && b.height > 0) {
                const fit = Math.min((size - 6) / b.width, (size - 6) / b.height, 1)
                portrait.scale.set(fit)
                portrait.x = x + (size - b.width * fit) / 2 - b.x * fit
                portrait.y = y + (size - b.height * fit) / 2 - b.y * fit
            }
        } else if (side.spriteId) {
            const sprite = new BasicSprite({
                name: side.spriteId,
                scale: 0.9,
                color: 0xffffff,
                animated: false,
                rotating: false,
            })
            content.addChild(sprite)
            centerSpriteIn(sprite, x, y, size)
        }
    }

    // The exchange between the two parties: a bronze medallion on a groove running across the panel,
    // carrying the wheel's swap glyph turned to point along the axis joining them.
    private drawTradeArrow = (content: Graphics, cx: number, cy: number) => {
        const r = 12
        if (!this.horizontalPanel) {
            groove(content, 16, cx - r - 6, cy)
            groove(content, cx + r + 6, this.panelW - 16, cy)
        }
        content.lineStyle(0)
        content.beginFill(0x000000, 0.45).drawCircle(cx, cy + 1.5, r + 0.5).endFill()
        content.beginFill(RIM, 1).drawCircle(cx, cy, r).endFill()
        content.lineStyle(1, TIMBER_EDGE, 0.9).drawCircle(cx, cy, r)
        content.lineStyle(0)
        content.beginFill(0x110c08, 1).drawCircle(cx, cy, r - 2.4).endFill()
        const swap = glyph("swap", cx, cy, 15, COLOR.gold)
        if (!this.horizontalPanel) {
            swap.rotation = Math.PI / 2
        }
        content.addChild(swap)
    }
}
